@file:Suppress("ComplexRedundantLet", "SimpleRedundantLet", "unused", "UnnecessaryVariable", "PropertyName", "RedundantVisibilityModifier")

package com.steamstreet.vegasful.graphql.api.client

import com.steamstreet.dates.DurationSerializer
import com.steamstreet.dates.InstantSerializer
import com.steamstreet.graphkt.ID
import com.steamstreet.graphkt.client.GraphQLResponse
import com.steamstreet.vegasful.graphql.api.AWSDate
import com.steamstreet.vegasful.graphql.api.AWSDateTime
import com.steamstreet.vegasful.graphql.api.AWSTime
import com.steamstreet.vegasful.graphql.api.ContentPanelFlag
import com.steamstreet.vegasful.graphql.api.ContentPanelType
import com.steamstreet.vegasful.graphql.api.ContentSource
import com.steamstreet.vegasful.graphql.api.ContentViewEntityAction
import com.steamstreet.vegasful.graphql.api.ContentViewLinkType
import com.steamstreet.vegasful.graphql.api.ContentViewType
import com.steamstreet.vegasful.graphql.api.ConversationStage
import com.steamstreet.vegasful.graphql.api.ConversationStatus
import com.steamstreet.vegasful.graphql.api.DateRangePreset
import com.steamstreet.vegasful.graphql.api.DisplayOrder
import com.steamstreet.vegasful.graphql.api.EntitySource
import com.steamstreet.vegasful.graphql.api.EventDuration
import com.steamstreet.vegasful.graphql.api.EventStream
import com.steamstreet.vegasful.graphql.api.ImageVariant
import com.steamstreet.vegasful.graphql.api.InteractionType
import com.steamstreet.vegasful.graphql.api.PerformerStatus
import com.steamstreet.vegasful.graphql.api.SocialImageState
import com.steamstreet.vegasful.graphql.api.SortOrder
import com.steamstreet.vegasful.graphql.api.SourceEntityType
import com.steamstreet.vegasful.graphql.api.SourceMode
import com.steamstreet.vegasful.graphql.api.ThingStatus
import com.steamstreet.vegasful.graphql.api.VenueStatus
import com.steamstreet.vegasful.graphql.api.Weekday
import com.steamstreet.vegasful.graphql.api.json
import kotlin.Boolean
import kotlin.Float
import kotlin.Int
import kotlin.NullPointerException
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlinx.datetime.serializers.LocalDateIso8601Serializer
import kotlinx.datetime.serializers.LocalTimeIso8601Serializer
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.boolean
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.float
import kotlinx.serialization.json.int
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

public interface Account {
  public val __typename: String

  public val createDate: AWSDateTime

  public val registerDevice: Boolean

  public val unregisterDevice: Boolean
}

public class AccountResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Account {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val createDate: AWSDateTime
    get() {
      _response.throwIfError("createDate")
      return (_element["createDate"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("Account: createDate")
    }

  override val registerDevice: Boolean
    get() {
      _response.throwIfError("registerDevice")
      return (_element["registerDevice"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("Account: registerDevice")
    }

  override val unregisterDevice: Boolean
    get() {
      _response.throwIfError("unregisterDevice")
      return (_element["unregisterDevice"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("Account: unregisterDevice")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Accounts {
  public val __typename: String

  public val createAnonymous: CreateAccountResponse

  public val me: Account?

  public val refresh: CreateAccountResponse
}

public class AccountsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Accounts {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val createAnonymous: CreateAccountResponse
    get() {
      _response.throwIfError("createAnonymous")
      return (_element["createAnonymous"])?.takeIf { it !is JsonNull }?.let { value0 ->
        CreateAccountResponseResponse(_response.forElement("createAnonymous"), value0.jsonObject)
      } ?: throw NullPointerException("Accounts: createAnonymous")
    }

  override val me: Account?
    get() {
      _response.throwIfError("me")
      return (_element["me"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AccountResponse(_response.forElement("me"), value0.jsonObject)
      }
    }

  override val refresh: CreateAccountResponse
    get() {
      _response.throwIfError("refresh")
      return (_element["refresh"])?.takeIf { it !is JsonNull }?.let { value0 ->
        CreateAccountResponseResponse(_response.forElement("refresh"), value0.jsonObject)
      } ?: throw NullPointerException("Accounts: refresh")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Address {
  public val __typename: String

  public val city: String?

  public val country: String?

  public val state: String?

  public val street: String?

  public val zip: String?
}

public class AddressResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Address {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val city: String?
    get() {
      _response.throwIfError("city")
      return (_element["city"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val country: String?
    get() {
      _response.throwIfError("country")
      return (_element["country"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val state: String?
    get() {
      _response.throwIfError("state")
      return (_element["state"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val street: String?
    get() {
      _response.throwIfError("street")
      return (_element["street"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val zip: String?
    get() {
      _response.throwIfError("zip")
      return (_element["zip"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminArticle {
  public val __typename: String

  public val content: String?

  public val created: AWSDateTime

  public val delete: Boolean

  public val id: ID

  public val images: List<Image>?

  public val links: List<Entity>?

  public val publish: AdminArticle

  public val published: AWSDateTime?

  public val resolvedLinks: List<Entity>

  public val summary: String?

  public val title: String?

  public val unpublish: AdminArticle

  public val update: AdminArticle

  public val updated: AWSDateTime
}

public class AdminArticleResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminArticle {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val content: String?
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val created: AWSDateTime
    get() {
      _response.throwIfError("created")
      return (_element["created"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("AdminArticle: created")
    }

  override val delete: Boolean
    get() {
      _response.throwIfError("delete")
      return (_element["delete"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminArticle: delete")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminArticle: id")
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminArticle: images")
        }
      }
    }

  override val links: List<Entity>?
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("links").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("links").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("links").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("links").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("links").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("links").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("links").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("links").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("AdminArticle: links")
        }
      }
    }

  override val publish: AdminArticle
    get() {
      _response.throwIfError("publish")
      return (_element["publish"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticleResponse(_response.forElement("publish"), value0.jsonObject)
      } ?: throw NullPointerException("AdminArticle: publish")
    }

  override val published: AWSDateTime?
    get() {
      _response.throwIfError("published")
      return (_element["published"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val resolvedLinks: List<Entity>
    get() {
      _response.throwIfError("resolvedLinks")
      return (_element["resolvedLinks"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("resolvedLinks").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("AdminArticle: resolvedLinks")
        }
      } ?: throw NullPointerException("AdminArticle: resolvedLinks")
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val title: String?
    get() {
      _response.throwIfError("title")
      return (_element["title"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val unpublish: AdminArticle
    get() {
      _response.throwIfError("unpublish")
      return (_element["unpublish"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticleResponse(_response.forElement("unpublish"), value0.jsonObject)
      } ?: throw NullPointerException("AdminArticle: unpublish")
    }

  override val update: AdminArticle
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticleResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminArticle: update")
    }

  override val updated: AWSDateTime
    get() {
      _response.throwIfError("updated")
      return (_element["updated"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("AdminArticle: updated")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminArticles {
  public val __typename: String

  public val article: AdminArticle

  public val content: AdminArticle

  public val drafts: List<AdminArticle>

  public val new: AdminArticle
}

public class AdminArticlesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminArticles {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val article: AdminArticle
    get() {
      _response.throwIfError("article")
      return (_element["article"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticleResponse(_response.forElement("article"), value0.jsonObject)
      } ?: throw NullPointerException("AdminArticles: article")
    }

  override val content: AdminArticle
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticleResponse(_response.forElement("content"), value0.jsonObject)
      } ?: throw NullPointerException("AdminArticles: content")
    }

  override val drafts: List<AdminArticle>
    get() {
      _response.throwIfError("drafts")
      return (_element["drafts"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminArticleResponse(_response.forElement("drafts").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminArticles: drafts")
        }
      } ?: throw NullPointerException("AdminArticles: drafts")
    }

  override val new: AdminArticle
    get() {
      _response.throwIfError("new")
      return (_element["new"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticleResponse(_response.forElement("new"), value0.jsonObject)
      } ?: throw NullPointerException("AdminArticles: new")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminEvent {
  public val __typename: String

  public val activatedTs: AWSDateTime?

  public val children: List<Event>

  public val deactivatedTs: AWSDateTime?

  public val disable: Boolean

  public val generateSocialImage: String

  public val id: ID

  public val layer: AdminEventLayer?

  public val layers: List<AdminEventLayer>

  public val local: AdminEventLayer

  public val parent: Event?

  public val rebuild: Boolean

  public val resolved: Event

  public val review: String?

  public val socialImageStatus: SocialImageStatus
}

public class AdminEventResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminEvent {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val activatedTs: AWSDateTime?
    get() {
      _response.throwIfError("activatedTs")
      return (_element["activatedTs"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val children: List<Event>
    get() {
      _response.throwIfError("children")
      return (_element["children"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("children").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEvent: children")
        }
      } ?: throw NullPointerException("AdminEvent: children")
    }

  override val deactivatedTs: AWSDateTime?
    get() {
      _response.throwIfError("deactivatedTs")
      return (_element["deactivatedTs"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val disable: Boolean
    get() {
      _response.throwIfError("disable")
      return (_element["disable"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminEvent: disable")
    }

  override val generateSocialImage: String
    get() {
      _response.throwIfError("generateSocialImage")
      return (_element["generateSocialImage"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminEvent: generateSocialImage")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminEvent: id")
    }

  override val layer: AdminEventLayer?
    get() {
      _response.throwIfError("layer")
      return (_element["layer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventLayerResponse(_response.forElement("layer"), value0.jsonObject)
      }
    }

  override val layers: List<AdminEventLayer>
    get() {
      _response.throwIfError("layers")
      return (_element["layers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminEventLayerResponse(_response.forElement("layers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEvent: layers")
        }
      } ?: throw NullPointerException("AdminEvent: layers")
    }

  override val local: AdminEventLayer
    get() {
      _response.throwIfError("local")
      return (_element["local"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventLayerResponse(_response.forElement("local"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEvent: local")
    }

  override val parent: Event?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val rebuild: Boolean
    get() {
      _response.throwIfError("rebuild")
      return (_element["rebuild"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminEvent: rebuild")
    }

  override val resolved: Event
    get() {
      _response.throwIfError("resolved")
      return (_element["resolved"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventResponse(_response.forElement("resolved"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEvent: resolved")
    }

  override val review: String?
    get() {
      _response.throwIfError("review")
      return (_element["review"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val socialImageStatus: SocialImageStatus
    get() {
      _response.throwIfError("socialImageStatus")
      return (_element["socialImageStatus"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SocialImageStatusResponse(_response.forElement("socialImageStatus"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEvent: socialImageStatus")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminEventLayer {
  public val __typename: String

  public val aliases: List<String>?

  public val contentView: String?

  public val date: AWSDate?

  public val description: String?

  public val duration: Int?

  public val excluded: Boolean?

  public val excludedImages: List<Image>

  public val excludedTags: List<Tag>

  public val expiration: AWSDateTime?

  public val images: List<Image>?

  public val lastUpdated: AWSDateTime?

  public val layerId: String?

  public val name: String?

  public val performers: List<Performer>?

  public val rankingScore: Int?

  public val sourceUrl: String?

  public val suggestedPerformers: List<String>?

  public val tagline: String?

  public val tags: List<Tag>?

  public val ticketsUrl: String?

  public val time: AWSTime?

  public val update: AdminEventLayer

  public val venue: Venue?
}

public class AdminEventLayerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminEventLayer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      return (_element["aliases"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("AdminEventLayer: aliases")
        }
      }
    }

  override val contentView: String?
    get() {
      _response.throwIfError("contentView")
      return (_element["contentView"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val date: AWSDate?
    get() {
      _response.throwIfError("date")
      return (_element["date"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalDateIso8601Serializer, value0)
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val duration: Int?
    get() {
      _response.throwIfError("duration")
      return (_element["duration"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val excluded: Boolean?
    get() {
      _response.throwIfError("excluded")
      return (_element["excluded"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val excludedImages: List<Image>
    get() {
      _response.throwIfError("excludedImages")
      return (_element["excludedImages"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("excludedImages").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEventLayer: excludedImages")
        }
      } ?: throw NullPointerException("AdminEventLayer: excludedImages")
    }

  override val excludedTags: List<Tag>
    get() {
      _response.throwIfError("excludedTags")
      return (_element["excludedTags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("excludedTags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEventLayer: excludedTags")
        }
      } ?: throw NullPointerException("AdminEventLayer: excludedTags")
    }

  override val expiration: AWSDateTime?
    get() {
      _response.throwIfError("expiration")
      return (_element["expiration"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEventLayer: images")
        }
      }
    }

  override val lastUpdated: AWSDateTime?
    get() {
      _response.throwIfError("lastUpdated")
      return (_element["lastUpdated"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val layerId: String?
    get() {
      _response.throwIfError("layerId")
      return (_element["layerId"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val performers: List<Performer>?
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            PerformerResponse(_response.forElement("performers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEventLayer: performers")
        }
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val sourceUrl: String?
    get() {
      _response.throwIfError("sourceUrl")
      return (_element["sourceUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val suggestedPerformers: List<String>?
    get() {
      _response.throwIfError("suggestedPerformers")
      return (_element["suggestedPerformers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("AdminEventLayer: suggestedPerformers")
        }
      }
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      return (_element["tagline"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEventLayer: tags")
        }
      }
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      return (_element["time"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      }
    }

  override val update: AdminEventLayer
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventLayerResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEventLayer: update")
    }

  override val venue: Venue?
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("venue"), value0.jsonObject)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminEvents {
  public val __typename: String

  public val add: AdminEvent

  public val enhance: EventUpdate?

  public val event: AdminEvent

  public val forReview: List<Event>

  public val merge: AdminEvent

  public val reviewNext: AdminEvent?
}

public class AdminEventsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminEvents {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val add: AdminEvent
    get() {
      _response.throwIfError("add")
      return (_element["add"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventResponse(_response.forElement("add"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEvents: add")
    }

  override val enhance: EventUpdate?
    get() {
      _response.throwIfError("enhance")
      return (_element["enhance"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventUpdateResponse(_response.forElement("enhance"), value0.jsonObject)
      }
    }

  override val event: AdminEvent
    get() {
      _response.throwIfError("event")
      return (_element["event"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventResponse(_response.forElement("event"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEvents: event")
    }

  override val forReview: List<Event>
    get() {
      _response.throwIfError("forReview")
      return (_element["forReview"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("forReview").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminEvents: forReview")
        }
      } ?: throw NullPointerException("AdminEvents: forReview")
    }

  override val merge: AdminEvent
    get() {
      _response.throwIfError("merge")
      return (_element["merge"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventResponse(_response.forElement("merge"), value0.jsonObject)
      } ?: throw NullPointerException("AdminEvents: merge")
    }

  override val reviewNext: AdminEvent?
    get() {
      _response.throwIfError("reviewNext")
      return (_element["reviewNext"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventResponse(_response.forElement("reviewNext"), value0.jsonObject)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminImages {
  public val __typename: String

  public val upload: Image
}

public class AdminImagesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminImages {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val upload: Image
    get() {
      _response.throwIfError("upload")
      return (_element["upload"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ImageResponse(_response.forElement("upload"), value0.jsonObject)
      } ?: throw NullPointerException("AdminImages: upload")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminLoader {
  public val __typename: String

  public val execute: Boolean

  public val id: ID

  public val lastLoad: LoaderExecution?

  public val logs: List<LogEvent>

  public val module: AdminLoaderModule?

  public val name: String

  public val parameters: String?

  public val publish: Boolean

  public val readInternalData: String?

  public val recentExecutions: List<LoaderExecution>

  public val reprocess: Boolean

  public val saveInternalData: Boolean?

  public val schedule: String?

  public val update: AdminLoader
}

public class AdminLoaderResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminLoader {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val execute: Boolean
    get() {
      _response.throwIfError("execute")
      return (_element["execute"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminLoader: execute")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminLoader: id")
    }

  override val lastLoad: LoaderExecution?
    get() {
      _response.throwIfError("lastLoad")
      return (_element["lastLoad"])?.takeIf { it !is JsonNull }?.let { value0 ->
        LoaderExecutionResponse(_response.forElement("lastLoad"), value0.jsonObject)
      }
    }

  override val logs: List<LogEvent>
    get() {
      _response.throwIfError("logs")
      return (_element["logs"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            LogEventResponse(_response.forElement("logs").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminLoader: logs")
        }
      } ?: throw NullPointerException("AdminLoader: logs")
    }

  override val module: AdminLoaderModule?
    get() {
      _response.throwIfError("module")
      return (_element["module"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLoaderModuleResponse(_response.forElement("module"), value0.jsonObject)
      }
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminLoader: name")
    }

  override val parameters: String?
    get() {
      _response.throwIfError("parameters")
      return (_element["parameters"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val publish: Boolean
    get() {
      _response.throwIfError("publish")
      return (_element["publish"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminLoader: publish")
    }

  override val readInternalData: String?
    get() {
      _response.throwIfError("readInternalData")
      return (_element["readInternalData"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val recentExecutions: List<LoaderExecution>
    get() {
      _response.throwIfError("recentExecutions")
      return (_element["recentExecutions"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            LoaderExecutionResponse(_response.forElement("recentExecutions").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminLoader: recentExecutions")
        }
      } ?: throw NullPointerException("AdminLoader: recentExecutions")
    }

  override val reprocess: Boolean
    get() {
      _response.throwIfError("reprocess")
      return (_element["reprocess"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminLoader: reprocess")
    }

  override val saveInternalData: Boolean?
    get() {
      _response.throwIfError("saveInternalData")
      return (_element["saveInternalData"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val schedule: String?
    get() {
      _response.throwIfError("schedule")
      return (_element["schedule"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val update: AdminLoader
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLoaderResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminLoader: update")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminLoaderModule {
  public val __typename: String

  public val id: ID

  public val name: String
}

public class AdminLoaderModuleResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminLoaderModule {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminLoaderModule: id")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminLoaderModule: name")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminLoaders {
  public val __typename: String

  public val deregister: Boolean

  public val loader: AdminLoader

  public val loaders: List<AdminLoader>

  public val module: AdminLoaderModule

  public val modules: List<AdminLoaderModule>

  public val registerLoader: AdminLoader
}

public class AdminLoadersResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminLoaders {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val deregister: Boolean
    get() {
      _response.throwIfError("deregister")
      return (_element["deregister"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminLoaders: deregister")
    }

  override val loader: AdminLoader
    get() {
      _response.throwIfError("loader")
      return (_element["loader"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLoaderResponse(_response.forElement("loader"), value0.jsonObject)
      } ?: throw NullPointerException("AdminLoaders: loader")
    }

  override val loaders: List<AdminLoader>
    get() {
      _response.throwIfError("loaders")
      return (_element["loaders"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminLoaderResponse(_response.forElement("loaders").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminLoaders: loaders")
        }
      } ?: throw NullPointerException("AdminLoaders: loaders")
    }

  override val module: AdminLoaderModule
    get() {
      _response.throwIfError("module")
      return (_element["module"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLoaderModuleResponse(_response.forElement("module"), value0.jsonObject)
      } ?: throw NullPointerException("AdminLoaders: module")
    }

  override val modules: List<AdminLoaderModule>
    get() {
      _response.throwIfError("modules")
      return (_element["modules"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminLoaderModuleResponse(_response.forElement("modules").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminLoaders: modules")
        }
      } ?: throw NullPointerException("AdminLoaders: modules")
    }

  override val registerLoader: AdminLoader
    get() {
      _response.throwIfError("registerLoader")
      return (_element["registerLoader"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLoaderResponse(_response.forElement("registerLoader"), value0.jsonObject)
      } ?: throw NullPointerException("AdminLoaders: registerLoader")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminLogs {
  public val __typename: String

  public val results: LogQueryResult

  public val startQuery: ID
}

public class AdminLogsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminLogs {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val results: LogQueryResult
    get() {
      _response.throwIfError("results")
      return (_element["results"])?.takeIf { it !is JsonNull }?.let { value0 ->
        LogQueryResultResponse(_response.forElement("results"), value0.jsonObject)
      } ?: throw NullPointerException("AdminLogs: results")
    }

  override val startQuery: ID
    get() {
      _response.throwIfError("startQuery")
      return (_element["startQuery"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminLogs: startQuery")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminMutation {
  public val __typename: String

  public val articles: AdminArticles

  public val domain: String?

  public val events: AdminEvents

  public val images: AdminImages

  public val loaders: AdminLoaders

  public val logs: AdminLogs

  public val performers: AdminPerformers

  public val tags: AdminTags

  public val thingsToDo: AdminThingsToDo

  public val venues: AdminVenues
}

public class AdminMutationResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminMutation {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val articles: AdminArticles
    get() {
      _response.throwIfError("articles")
      return (_element["articles"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminArticlesResponse(_response.forElement("articles"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: articles")
    }

  override val domain: String?
    get() {
      _response.throwIfError("domain")
      return (_element["domain"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: AdminEvents
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminEventsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: events")
    }

  override val images: AdminImages
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminImagesResponse(_response.forElement("images"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: images")
    }

  override val loaders: AdminLoaders
    get() {
      _response.throwIfError("loaders")
      return (_element["loaders"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLoadersResponse(_response.forElement("loaders"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: loaders")
    }

  override val logs: AdminLogs
    get() {
      _response.throwIfError("logs")
      return (_element["logs"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminLogsResponse(_response.forElement("logs"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: logs")
    }

  override val performers: AdminPerformers
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminPerformersResponse(_response.forElement("performers"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: performers")
    }

  override val tags: AdminTags
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminTagsResponse(_response.forElement("tags"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: tags")
    }

  override val thingsToDo: AdminThingsToDo
    get() {
      _response.throwIfError("thingsToDo")
      return (_element["thingsToDo"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminThingsToDoResponse(_response.forElement("thingsToDo"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: thingsToDo")
    }

  override val venues: AdminVenues
    get() {
      _response.throwIfError("venues")
      return (_element["venues"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminVenuesResponse(_response.forElement("venues"), value0.jsonObject)
      } ?: throw NullPointerException("AdminMutation: venues")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminPerformer {
  public val __typename: String

  public val id: ID

  public val layer: AdminPerformerLayer?

  public val layers: List<AdminPerformerLayer?>

  public val local: AdminPerformerLayer

  public val resolved: Performer
}

public class AdminPerformerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminPerformer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminPerformer: id")
    }

  override val layer: AdminPerformerLayer?
    get() {
      _response.throwIfError("layer")
      return (_element["layer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminPerformerLayerResponse(_response.forElement("layer"), value0.jsonObject)
      }
    }

  override val layers: List<AdminPerformerLayer?>
    get() {
      _response.throwIfError("layers")
      return (_element["layers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminPerformerLayerResponse(_response.forElement("layers").forElement(index1.toString()), value2.jsonObject)
          }
        }
      } ?: throw NullPointerException("AdminPerformer: layers")
    }

  override val local: AdminPerformerLayer
    get() {
      _response.throwIfError("local")
      return (_element["local"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminPerformerLayerResponse(_response.forElement("local"), value0.jsonObject)
      } ?: throw NullPointerException("AdminPerformer: local")
    }

  override val resolved: Performer
    get() {
      _response.throwIfError("resolved")
      return (_element["resolved"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformerResponse(_response.forElement("resolved"), value0.jsonObject)
      } ?: throw NullPointerException("AdminPerformer: resolved")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminPerformerLayer {
  public val __typename: String

  public val aliases: List<String>?

  public val description: String?

  public val images: List<Image>?

  public val name: String?

  public val rankingScore: Int?

  public val status: PerformerStatus?

  public val summary: String?

  public val tags: List<Tag>?

  public val update: AdminPerformerLayer

  public val wikiIdentifier: String?
}

public class AdminPerformerLayerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminPerformerLayer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      return (_element["aliases"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("AdminPerformerLayer: aliases")
        }
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminPerformerLayer: images")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val status: PerformerStatus?
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformerStatus.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminPerformerLayer: tags")
        }
      }
    }

  override val update: AdminPerformerLayer
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminPerformerLayerResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminPerformerLayer: update")
    }

  override val wikiIdentifier: String?
    get() {
      _response.throwIfError("wikiIdentifier")
      return (_element["wikiIdentifier"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminPerformers {
  public val __typename: String

  public val add: AdminPerformer

  public val enhance: PerformerUpdate?

  public val performer: AdminPerformer
}

public class AdminPerformersResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminPerformers {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val add: AdminPerformer
    get() {
      _response.throwIfError("add")
      return (_element["add"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminPerformerResponse(_response.forElement("add"), value0.jsonObject)
      } ?: throw NullPointerException("AdminPerformers: add")
    }

  override val enhance: PerformerUpdate?
    get() {
      _response.throwIfError("enhance")
      return (_element["enhance"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformerUpdateResponse(_response.forElement("enhance"), value0.jsonObject)
      }
    }

  override val performer: AdminPerformer
    get() {
      _response.throwIfError("performer")
      return (_element["performer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminPerformerResponse(_response.forElement("performer"), value0.jsonObject)
      } ?: throw NullPointerException("AdminPerformers: performer")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminTag {
  public val __typename: String

  public val children: List<Tag>?

  public val id: ID

  public val layer: AdminTagLayer?

  public val layers: List<AdminTagLayer?>

  public val local: AdminTagLayer

  public val parent: Tag?

  public val resolved: Tag
}

public class AdminTagResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminTag {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val children: List<Tag>?
    get() {
      _response.throwIfError("children")
      return (_element["children"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("children").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminTag: children")
        }
      }
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminTag: id")
    }

  override val layer: AdminTagLayer?
    get() {
      _response.throwIfError("layer")
      return (_element["layer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminTagLayerResponse(_response.forElement("layer"), value0.jsonObject)
      }
    }

  override val layers: List<AdminTagLayer?>
    get() {
      _response.throwIfError("layers")
      return (_element["layers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminTagLayerResponse(_response.forElement("layers").forElement(index1.toString()), value2.jsonObject)
          }
        }
      } ?: throw NullPointerException("AdminTag: layers")
    }

  override val local: AdminTagLayer
    get() {
      _response.throwIfError("local")
      return (_element["local"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminTagLayerResponse(_response.forElement("local"), value0.jsonObject)
      } ?: throw NullPointerException("AdminTag: local")
    }

  override val parent: Tag?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val resolved: Tag
    get() {
      _response.throwIfError("resolved")
      return (_element["resolved"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagResponse(_response.forElement("resolved"), value0.jsonObject)
      } ?: throw NullPointerException("AdminTag: resolved")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminTagLayer {
  public val __typename: String

  public val aiPromptInput: String?

  public val childLimit: Int?

  public val contentView: String?

  public val description: String?

  public val images: List<Image>?

  public val impliedTags: List<Tag>?

  public val name: String?

  public val pageTitle: String?

  public val parent: Tag?

  public val personalize: Boolean?

  public val rankingScore: Int?

  public val shadow: Boolean?

  public val update: AdminTagLayer
}

public class AdminTagLayerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminTagLayer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val aiPromptInput: String?
    get() {
      _response.throwIfError("aiPromptInput")
      return (_element["aiPromptInput"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val childLimit: Int?
    get() {
      _response.throwIfError("childLimit")
      return (_element["childLimit"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val contentView: String?
    get() {
      _response.throwIfError("contentView")
      return (_element["contentView"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminTagLayer: images")
        }
      }
    }

  override val impliedTags: List<Tag>?
    get() {
      _response.throwIfError("impliedTags")
      return (_element["impliedTags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("impliedTags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminTagLayer: impliedTags")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val pageTitle: String?
    get() {
      _response.throwIfError("pageTitle")
      return (_element["pageTitle"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val parent: Tag?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val personalize: Boolean?
    get() {
      _response.throwIfError("personalize")
      return (_element["personalize"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val shadow: Boolean?
    get() {
      _response.throwIfError("shadow")
      return (_element["shadow"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val update: AdminTagLayer
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminTagLayerResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminTagLayer: update")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminTags {
  public val __typename: String

  public val add: AdminTag

  public val all: List<AdminTag>

  public val tag: AdminTag
}

public class AdminTagsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminTags {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val add: AdminTag
    get() {
      _response.throwIfError("add")
      return (_element["add"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminTagResponse(_response.forElement("add"), value0.jsonObject)
      } ?: throw NullPointerException("AdminTags: add")
    }

  override val all: List<AdminTag>
    get() {
      _response.throwIfError("all")
      return (_element["all"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminTagResponse(_response.forElement("all").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminTags: all")
        }
      } ?: throw NullPointerException("AdminTags: all")
    }

  override val tag: AdminTag
    get() {
      _response.throwIfError("tag")
      return (_element["tag"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminTagResponse(_response.forElement("tag"), value0.jsonObject)
      } ?: throw NullPointerException("AdminTags: tag")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminThingLayer {
  public val __typename: String

  public val aliases: List<String>?

  public val description: String?

  public val hours: String?

  public val hoursSources: String?

  public val images: List<Image>

  public val links: List<SocialLink>?

  public val name: String?

  public val priceFrom: Int?

  public val rankingScore: Int?

  public val status: ThingStatus?

  public val summary: String?

  public val tagline: String?

  public val tags: List<Tag>?

  public val ticketsUrl: String?

  public val typicalDuration: Int?

  public val update: AdminThingLayer

  public val venue: Venue?
}

public class AdminThingLayerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminThingLayer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      return (_element["aliases"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("AdminThingLayer: aliases")
        }
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val hours: String?
    get() {
      _response.throwIfError("hours")
      return (_element["hours"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val hoursSources: String?
    get() {
      _response.throwIfError("hoursSources")
      return (_element["hoursSources"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminThingLayer: images")
        }
      } ?: throw NullPointerException("AdminThingLayer: images")
    }

  override val links: List<SocialLink>?
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SocialLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminThingLayer: links")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val priceFrom: Int?
    get() {
      _response.throwIfError("priceFrom")
      return (_element["priceFrom"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val status: ThingStatus?
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ThingStatus.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      return (_element["tagline"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminThingLayer: tags")
        }
      }
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val typicalDuration: Int?
    get() {
      _response.throwIfError("typicalDuration")
      return (_element["typicalDuration"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val update: AdminThingLayer
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminThingLayerResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminThingLayer: update")
    }

  override val venue: Venue?
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("venue"), value0.jsonObject)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminThingToDo {
  public val __typename: String

  public val delete: Boolean

  public val id: ID

  public val layer: AdminThingLayer?

  public val layers: List<AdminThingLayer>

  public val local: AdminThingLayer

  public val resolved: ThingToDo
}

public class AdminThingToDoResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminThingToDo {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val delete: Boolean
    get() {
      _response.throwIfError("delete")
      return (_element["delete"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("AdminThingToDo: delete")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminThingToDo: id")
    }

  override val layer: AdminThingLayer?
    get() {
      _response.throwIfError("layer")
      return (_element["layer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminThingLayerResponse(_response.forElement("layer"), value0.jsonObject)
      }
    }

  override val layers: List<AdminThingLayer>
    get() {
      _response.throwIfError("layers")
      return (_element["layers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminThingLayerResponse(_response.forElement("layers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminThingToDo: layers")
        }
      } ?: throw NullPointerException("AdminThingToDo: layers")
    }

  override val local: AdminThingLayer
    get() {
      _response.throwIfError("local")
      return (_element["local"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminThingLayerResponse(_response.forElement("local"), value0.jsonObject)
      } ?: throw NullPointerException("AdminThingToDo: local")
    }

  override val resolved: ThingToDo
    get() {
      _response.throwIfError("resolved")
      return (_element["resolved"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ThingToDoResponse(_response.forElement("resolved"), value0.jsonObject)
      } ?: throw NullPointerException("AdminThingToDo: resolved")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminThingsToDo {
  public val __typename: String

  public val add: AdminThingToDo

  public val all: List<AdminThingToDo>

  public val thing: AdminThingToDo
}

public class AdminThingsToDoResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminThingsToDo {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val add: AdminThingToDo
    get() {
      _response.throwIfError("add")
      return (_element["add"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminThingToDoResponse(_response.forElement("add"), value0.jsonObject)
      } ?: throw NullPointerException("AdminThingsToDo: add")
    }

  override val all: List<AdminThingToDo>
    get() {
      _response.throwIfError("all")
      return (_element["all"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminThingToDoResponse(_response.forElement("all").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminThingsToDo: all")
        }
      } ?: throw NullPointerException("AdminThingsToDo: all")
    }

  override val thing: AdminThingToDo
    get() {
      _response.throwIfError("thing")
      return (_element["thing"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminThingToDoResponse(_response.forElement("thing"), value0.jsonObject)
      } ?: throw NullPointerException("AdminThingsToDo: thing")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminVenue {
  public val __typename: String

  public val children: List<Venue>

  public val id: ID

  public val layer: AdminVenueLayer?

  public val layers: List<AdminVenueLayer>

  public val local: AdminVenueLayer

  public val parent: Venue?

  public val resolved: Venue
}

public class AdminVenueResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminVenue {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val children: List<Venue>
    get() {
      _response.throwIfError("children")
      return (_element["children"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            VenueResponse(_response.forElement("children").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminVenue: children")
        }
      } ?: throw NullPointerException("AdminVenue: children")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("AdminVenue: id")
    }

  override val layer: AdminVenueLayer?
    get() {
      _response.throwIfError("layer")
      return (_element["layer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminVenueLayerResponse(_response.forElement("layer"), value0.jsonObject)
      }
    }

  override val layers: List<AdminVenueLayer>
    get() {
      _response.throwIfError("layers")
      return (_element["layers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            AdminVenueLayerResponse(_response.forElement("layers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminVenue: layers")
        }
      } ?: throw NullPointerException("AdminVenue: layers")
    }

  override val local: AdminVenueLayer
    get() {
      _response.throwIfError("local")
      return (_element["local"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminVenueLayerResponse(_response.forElement("local"), value0.jsonObject)
      } ?: throw NullPointerException("AdminVenue: local")
    }

  override val parent: Venue?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val resolved: Venue
    get() {
      _response.throwIfError("resolved")
      return (_element["resolved"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("resolved"), value0.jsonObject)
      } ?: throw NullPointerException("AdminVenue: resolved")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminVenueLayer {
  public val __typename: String

  public val address: Address?

  public val aliases: List<String>?

  public val contentView: String?

  public val description: String?

  public val eventStream: EventStream?

  public val images: List<Image>

  public val links: List<SocialLink>?

  public val location: GPSCoordinates?

  public val name: String?

  public val parking: String?

  public val rankingScore: Int?

  public val redirect: Venue?

  public val status: VenueStatus?

  public val tags: List<Tag>?

  public val ticketsUrl: String?

  public val update: AdminVenueLayer
}

public class AdminVenueLayerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminVenueLayer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val address: Address?
    get() {
      _response.throwIfError("address")
      return (_element["address"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AddressResponse(_response.forElement("address"), value0.jsonObject)
      }
    }

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      return (_element["aliases"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("AdminVenueLayer: aliases")
        }
      }
    }

  override val contentView: String?
    get() {
      _response.throwIfError("contentView")
      return (_element["contentView"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val eventStream: EventStream?
    get() {
      _response.throwIfError("eventStream")
      return (_element["eventStream"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventStream.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminVenueLayer: images")
        }
      } ?: throw NullPointerException("AdminVenueLayer: images")
    }

  override val links: List<SocialLink>?
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SocialLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminVenueLayer: links")
        }
      }
    }

  override val location: GPSCoordinates?
    get() {
      _response.throwIfError("location")
      return (_element["location"])?.takeIf { it !is JsonNull }?.let { value0 ->
        GPSCoordinatesResponse(_response.forElement("location"), value0.jsonObject)
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      return (_element["parking"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val redirect: Venue?
    get() {
      _response.throwIfError("redirect")
      return (_element["redirect"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("redirect"), value0.jsonObject)
      }
    }

  override val status: VenueStatus?
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueStatus.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("AdminVenueLayer: tags")
        }
      }
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val update: AdminVenueLayer
    get() {
      _response.throwIfError("update")
      return (_element["update"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminVenueLayerResponse(_response.forElement("update"), value0.jsonObject)
      } ?: throw NullPointerException("AdminVenueLayer: update")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AdminVenues {
  public val __typename: String

  public val add: AdminVenue

  public val venue: AdminVenue
}

public class AdminVenuesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AdminVenues {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val add: AdminVenue
    get() {
      _response.throwIfError("add")
      return (_element["add"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminVenueResponse(_response.forElement("add"), value0.jsonObject)
      } ?: throw NullPointerException("AdminVenues: add")
    }

  override val venue: AdminVenue
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminVenueResponse(_response.forElement("venue"), value0.jsonObject)
      } ?: throw NullPointerException("AdminVenues: venue")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Article : Entity {
  override val __typename: String

  public val content: String?

  public val created: AWSDateTime

  override val description: String?

  override val events: EventSearchResults

  override val id: ID

  override val images: List<Image>

  public val linkedEntities: List<Entity>

  override val name: String?

  override val path: String?

  public val published: AWSDateTime?

  public val summary: String

  override val tags: List<Tag>?

  public val title: String

  public val updated: AWSDateTime
}

public class ArticleResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Article {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val content: String?
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val created: AWSDateTime
    get() {
      _response.throwIfError("created")
      return (_element["created"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("Article: created")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Article: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Article: id")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Article: images")
        }
      } ?: throw NullPointerException("Article: images")
    }

  override val linkedEntities: List<Entity>
    get() {
      _response.throwIfError("linkedEntities")
      return (_element["linkedEntities"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("linkedEntities").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("Article: linkedEntities")
        }
      } ?: throw NullPointerException("Article: linkedEntities")
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val path: String?
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val published: AWSDateTime?
    get() {
      _response.throwIfError("published")
      return (_element["published"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val summary: String
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Article: summary")
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Article: tags")
        }
      }
    }

  override val title: String
    get() {
      _response.throwIfError("title")
      return (_element["title"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Article: title")
    }

  override val updated: AWSDateTime
    get() {
      _response.throwIfError("updated")
      return (_element["updated"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("Article: updated")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ArticleSearchResult {
  public val __typename: String

  public val articles: List<Article>
}

public class ArticleSearchResultResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ArticleSearchResult {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val articles: List<Article>
    get() {
      _response.throwIfError("articles")
      return (_element["articles"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ArticleResponse(_response.forElement("articles").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ArticleSearchResult: articles")
        }
      } ?: throw NullPointerException("ArticleSearchResult: articles")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Articles {
  public val __typename: String

  public val article: Article

  public val query: ArticleSearchResult

  public val recent: ArticleSearchResult
}

public class ArticlesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Articles {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val article: Article
    get() {
      _response.throwIfError("article")
      return (_element["article"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ArticleResponse(_response.forElement("article"), value0.jsonObject)
      } ?: throw NullPointerException("Articles: article")
    }

  override val query: ArticleSearchResult
    get() {
      _response.throwIfError("query")
      return (_element["query"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ArticleSearchResultResponse(_response.forElement("query"), value0.jsonObject)
      } ?: throw NullPointerException("Articles: query")
    }

  override val recent: ArticleSearchResult
    get() {
      _response.throwIfError("recent")
      return (_element["recent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ArticleSearchResultResponse(_response.forElement("recent"), value0.jsonObject)
      } ?: throw NullPointerException("Articles: recent")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AuxContent {
  public val __typename: String

  public val features: AuxFeatures

  public val onboarding: Onboarding
}

public class AuxContentResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AuxContent {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val features: AuxFeatures
    get() {
      _response.throwIfError("features")
      return (_element["features"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AuxFeaturesResponse(_response.forElement("features"), value0.jsonObject)
      } ?: throw NullPointerException("AuxContent: features")
    }

  override val onboarding: Onboarding
    get() {
      _response.throwIfError("onboarding")
      return (_element["onboarding"])?.takeIf { it !is JsonNull }?.let { value0 ->
        OnboardingResponse(_response.forElement("onboarding"), value0.jsonObject)
      } ?: throw NullPointerException("AuxContent: onboarding")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface AuxFeatures {
  public val __typename: String

  public val feature: ViewConfiguration
}

public class AuxFeaturesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : AuxFeatures {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val feature: ViewConfiguration
    get() {
      _response.throwIfError("feature")
      return (_element["feature"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ViewConfigurationResponse(_response.forElement("feature"), value0.jsonObject)
      } ?: throw NullPointerException("AuxFeatures: feature")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ClientSupport {
  public val __typename: String

  public val logClientEvent: Boolean

  public val onboarding: Onboarding
}

public class ClientSupportResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ClientSupport {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val logClientEvent: Boolean
    get() {
      _response.throwIfError("logClientEvent")
      return (_element["logClientEvent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("ClientSupport: logClientEvent")
    }

  override val onboarding: Onboarding
    get() {
      _response.throwIfError("onboarding")
      return (_element["onboarding"])?.takeIf { it !is JsonNull }?.let { value0 ->
        OnboardingResponse(_response.forElement("onboarding"), value0.jsonObject)
      } ?: throw NullPointerException("ClientSupport: onboarding")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ConciergePick {
  public val __typename: String

  public val dates: List<AWSDate>

  public val entity: Entity

  public val reason: String?

  public val time: AWSTime?
}

public class ConciergePickResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ConciergePick {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val dates: List<AWSDate>
    get() {
      _response.throwIfError("dates")
      return (_element["dates"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            json.decodeFromJsonElement(LocalDateIso8601Serializer, value2)
          } ?: throw NullPointerException("ConciergePick: dates")
        }
      } ?: throw NullPointerException("ConciergePick: dates")
    }

  override val entity: Entity
    get() {
      _response.throwIfError("entity")
      return (_element["entity"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonObject.let { element ->
          val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Article" -> ArticleResponse(_response.forElement("entity"), element)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entity"), element)
            "Event" -> EventResponse(_response.forElement("entity"), element)
            "Performer" -> PerformerResponse(_response.forElement("entity"), element)
            "Tag" -> TagResponse(_response.forElement("entity"), element)
            "ThingToDo" -> ThingToDoResponse(_response.forElement("entity"), element)
            "Venue" -> VenueResponse(_response.forElement("entity"), element)
            else -> EntityResponse(_response.forElement("entity"), element)
          }
        }
      } ?: throw NullPointerException("ConciergePick: entity")
    }

  override val reason: String?
    get() {
      _response.throwIfError("reason")
      return (_element["reason"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      return (_element["time"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Content {
  public val __typename: String

  public val entities: List<Entity>

  public val events: Events

  public val performers: Performers

  public val tags: Tags

  public val venues: Venues
}

public class ContentResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Content {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val entities: List<Entity>
    get() {
      _response.throwIfError("entities")
      return (_element["entities"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("entities").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("Content: entities")
        }
      } ?: throw NullPointerException("Content: entities")
    }

  override val events: Events
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Content: events")
    }

  override val performers: Performers
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformersResponse(_response.forElement("performers"), value0.jsonObject)
      } ?: throw NullPointerException("Content: performers")
    }

  override val tags: Tags
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagsResponse(_response.forElement("tags"), value0.jsonObject)
      } ?: throw NullPointerException("Content: tags")
    }

  override val venues: Venues
    get() {
      _response.throwIfError("venues")
      return (_element["venues"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenuesResponse(_response.forElement("venues"), value0.jsonObject)
      } ?: throw NullPointerException("Content: venues")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ContentPanel {
  public val __typename: String

  public val articleSource: String?

  public val content: String?

  public val displayOrder: DisplayOrder?

  public val entities: List<Entity>?

  public val entitiesSource: EntitySource?

  public val entityFilters: List<Entity>?

  public val filterTags: List<Tag>?

  public val flags: List<ContentPanelFlag>?

  public val id: ID

  public val images: List<Image>?

  public val links: List<ContentViewLink>?

  public val maxWidth: Int?

  public val minWidth: Int?

  public val source: PanelSource?

  public val subTitle: String?

  public val subTitleLink: String?

  public val title: String?

  public val titleLink: String?

  public val type: ContentPanelType
}

public class ContentPanelResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ContentPanel {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val articleSource: String?
    get() {
      _response.throwIfError("articleSource")
      return (_element["articleSource"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val content: String?
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val displayOrder: DisplayOrder?
    get() {
      _response.throwIfError("displayOrder")
      return (_element["displayOrder"])?.takeIf { it !is JsonNull }?.let { value0 ->
        DisplayOrder.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val entities: List<Entity>?
    get() {
      _response.throwIfError("entities")
      return (_element["entities"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("entities").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("ContentPanel: entities")
        }
      }
    }

  override val entitiesSource: EntitySource?
    get() {
      _response.throwIfError("entitiesSource")
      return (_element["entitiesSource"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EntitySource.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val entityFilters: List<Entity>?
    get() {
      _response.throwIfError("entityFilters")
      return (_element["entityFilters"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("entityFilters").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("ContentPanel: entityFilters")
        }
      }
    }

  override val filterTags: List<Tag>?
    get() {
      _response.throwIfError("filterTags")
      return (_element["filterTags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("filterTags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentPanel: filterTags")
        }
      }
    }

  override val flags: List<ContentPanelFlag>?
    get() {
      _response.throwIfError("flags")
      return (_element["flags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ContentPanelFlag.valueOf(value2.jsonPrimitive.content)
          } ?: throw NullPointerException("ContentPanel: flags")
        }
      }
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ContentPanel: id")
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentPanel: images")
        }
      }
    }

  override val links: List<ContentViewLink>?
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ContentViewLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentPanel: links")
        }
      }
    }

  override val maxWidth: Int?
    get() {
      _response.throwIfError("maxWidth")
      return (_element["maxWidth"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val minWidth: Int?
    get() {
      _response.throwIfError("minWidth")
      return (_element["minWidth"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val source: PanelSource?
    get() {
      _response.throwIfError("source")
      return (_element["source"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PanelSourceResponse(_response.forElement("source"), value0.jsonObject)
      }
    }

  override val subTitle: String?
    get() {
      _response.throwIfError("subTitle")
      return (_element["subTitle"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val subTitleLink: String?
    get() {
      _response.throwIfError("subTitleLink")
      return (_element["subTitleLink"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val title: String?
    get() {
      _response.throwIfError("title")
      return (_element["title"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val titleLink: String?
    get() {
      _response.throwIfError("titleLink")
      return (_element["titleLink"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val type: ContentPanelType
    get() {
      _response.throwIfError("type")
      return (_element["type"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentPanelType.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("ContentPanel: type")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ContentView {
  public val __typename: String

  public val content: String?

  public val images: List<Image>?

  public val links: List<ContentViewLink>?

  public val sections: List<ContentViewSection>?

  public val summary: String?

  public val title: String?
}

public class ContentViewResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ContentView {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val content: String?
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentView: images")
        }
      }
    }

  override val links: List<ContentViewLink>?
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ContentViewLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentView: links")
        }
      }
    }

  override val sections: List<ContentViewSection>?
    get() {
      _response.throwIfError("sections")
      return (_element["sections"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ContentViewSectionResponse(_response.forElement("sections").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentView: sections")
        }
      }
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val title: String?
    get() {
      _response.throwIfError("title")
      return (_element["title"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ContentViewEntity : Entity {
  override val __typename: String

  public val action: ContentViewEntityAction?

  public val containerName: String?

  public val containerPath: String?

  override val description: String?

  override val events: EventSearchResults

  override val id: ID

  override val images: List<Image>?

  override val name: String?

  override val path: String?

  override val tags: List<Tag>?
}

public class ContentViewEntityResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ContentViewEntity {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val action: ContentViewEntityAction?
    get() {
      _response.throwIfError("action")
      return (_element["action"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentViewEntityAction.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val containerName: String?
    get() {
      _response.throwIfError("containerName")
      return (_element["containerName"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val containerPath: String?
    get() {
      _response.throwIfError("containerPath")
      return (_element["containerPath"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("ContentViewEntity: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ContentViewEntity: id")
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentViewEntity: images")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val path: String?
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentViewEntity: tags")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ContentViewLink {
  public val __typename: String

  public val description: String?

  public val name: String

  public val path: String

  public val type: ContentViewLinkType
}

public class ContentViewLinkResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ContentViewLink {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ContentViewLink: name")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ContentViewLink: path")
    }

  override val type: ContentViewLinkType
    get() {
      _response.throwIfError("type")
      return (_element["type"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentViewLinkType.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("ContentViewLink: type")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ContentViewSection {
  public val __typename: String

  public val content: List<ContentViewEntity>?

  public val contentHtml: String?

  public val link: String?

  public val source: ContentSource?

  public val title: String

  public val type: ContentViewType
}

public class ContentViewSectionResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ContentViewSection {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val content: List<ContentViewEntity>?
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ContentViewEntityResponse(_response.forElement("content").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ContentViewSection: content")
        }
      }
    }

  override val contentHtml: String?
    get() {
      _response.throwIfError("contentHtml")
      return (_element["contentHtml"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val link: String?
    get() {
      _response.throwIfError("link")
      return (_element["link"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val source: ContentSource?
    get() {
      _response.throwIfError("source")
      return (_element["source"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentSource.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val title: String
    get() {
      _response.throwIfError("title")
      return (_element["title"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ContentViewSection: title")
    }

  override val type: ContentViewType
    get() {
      _response.throwIfError("type")
      return (_element["type"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentViewType.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("ContentViewSection: type")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ContentViews {
  public val __typename: String

  public val byId: ViewConfiguration

  public val view: ContentView
}

public class ContentViewsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ContentViews {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val byId: ViewConfiguration
    get() {
      _response.throwIfError("byId")
      return (_element["byId"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ViewConfigurationResponse(_response.forElement("byId"), value0.jsonObject)
      } ?: throw NullPointerException("ContentViews: byId")
    }

  override val view: ContentView
    get() {
      _response.throwIfError("view")
      return (_element["view"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentViewResponse(_response.forElement("view"), value0.jsonObject)
      } ?: throw NullPointerException("ContentViews: view")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Conversation {
  public val __typename: String

  public val id: ID

  public val message: String?

  public val more: List<Entity>?

  public val picks: List<ConciergePick>?

  public val stage: ConversationStage?

  public val status: ConversationStatus?

  public val turns: List<ConversationTurn>?
}

public class ConversationResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Conversation {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Conversation: id")
    }

  override val message: String?
    get() {
      _response.throwIfError("message")
      return (_element["message"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val more: List<Entity>?
    get() {
      _response.throwIfError("more")
      return (_element["more"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("more").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("more").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("more").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("more").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("Conversation: more")
        }
      }
    }

  override val picks: List<ConciergePick>?
    get() {
      _response.throwIfError("picks")
      return (_element["picks"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ConciergePickResponse(_response.forElement("picks").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Conversation: picks")
        }
      }
    }

  override val stage: ConversationStage?
    get() {
      _response.throwIfError("stage")
      return (_element["stage"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ConversationStage.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val status: ConversationStatus?
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ConversationStatus.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val turns: List<ConversationTurn>?
    get() {
      _response.throwIfError("turns")
      return (_element["turns"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ConversationTurnResponse(_response.forElement("turns").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Conversation: turns")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ConversationTurn {
  public val __typename: String

  public val message: String?

  public val more: List<Entity>?

  public val picks: List<ConciergePick>?

  public val query: String

  public val status: ConversationStatus
}

public class ConversationTurnResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ConversationTurn {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val message: String?
    get() {
      _response.throwIfError("message")
      return (_element["message"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val more: List<Entity>?
    get() {
      _response.throwIfError("more")
      return (_element["more"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("more").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("more").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("more").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("more").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("more").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("ConversationTurn: more")
        }
      }
    }

  override val picks: List<ConciergePick>?
    get() {
      _response.throwIfError("picks")
      return (_element["picks"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ConciergePickResponse(_response.forElement("picks").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ConversationTurn: picks")
        }
      }
    }

  override val query: String
    get() {
      _response.throwIfError("query")
      return (_element["query"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ConversationTurn: query")
    }

  override val status: ConversationStatus
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ConversationStatus.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("ConversationTurn: status")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Conversations {
  public val __typename: String

  public val conversation: Conversation?

  public val start: Conversation
}

public class ConversationsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Conversations {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val conversation: Conversation?
    get() {
      _response.throwIfError("conversation")
      return (_element["conversation"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ConversationResponse(_response.forElement("conversation"), value0.jsonObject)
      }
    }

  override val start: Conversation
    get() {
      _response.throwIfError("start")
      return (_element["start"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ConversationResponse(_response.forElement("start"), value0.jsonObject)
      } ?: throw NullPointerException("Conversations: start")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface CreateAccountResponse {
  public val __typename: String

  public val id: ID

  public val token: String
}

public class CreateAccountResponseResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : CreateAccountResponse {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("CreateAccountResponse: id")
    }

  override val token: String
    get() {
      _response.throwIfError("token")
      return (_element["token"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("CreateAccountResponse: token")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface DateRange {
  public val __typename: String

  public val from: String?

  public val preset: DateRangePreset?

  public val to: String?
}

public class DateRangeResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : DateRange {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val from: String?
    get() {
      _response.throwIfError("from")
      return (_element["from"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val preset: DateRangePreset?
    get() {
      _response.throwIfError("preset")
      return (_element["preset"])?.takeIf { it !is JsonNull }?.let { value0 ->
        DateRangePreset.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val to: String?
    get() {
      _response.throwIfError("to")
      return (_element["to"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Entity {
  public val __typename: String

  public val description: String?

  public val events: EventSearchResults

  public val id: ID

  public val images: List<Image>?

  public val name: String?

  public val path: String?

  public val tags: List<Tag>?
}

public class EntityResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Entity {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Entity: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Entity: id")
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Entity: images")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val path: String?
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Entity: tags")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Event : Entity {
  override val __typename: String

  public val active: Boolean

  public val children: List<Event>

  public val contentView: ViewConfiguration?

  public val date: AWSDate?

  override val description: String?

  public val disabled: Boolean

  public val duration: EventDuration?

  public val endDate: AWSDate?

  public val endTime: AWSTime?

  override val events: EventSearchResults

  override val id: ID

  override val images: List<Image>

  override val name: String

  public val parent: Event?

  public val parking: String?

  override val path: String

  public val performers: List<Performer>

  public val related: EventSearchResults

  public val score: Int?

  public val sourceUrl: String?

  public val tagline: String?

  override val tags: List<Tag>

  public val ticketsUrl: String?

  public val time: AWSTime?

  public val ts: AWSDateTime?

  public val venue: Venue?
}

public class EventResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Event {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val active: Boolean
    get() {
      _response.throwIfError("active")
      return (_element["active"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("Event: active")
    }

  override val children: List<Event>
    get() {
      _response.throwIfError("children")
      return (_element["children"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("children").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Event: children")
        }
      } ?: throw NullPointerException("Event: children")
    }

  override val contentView: ViewConfiguration?
    get() {
      _response.throwIfError("contentView")
      return (_element["contentView"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ViewConfigurationResponse(_response.forElement("contentView"), value0.jsonObject)
      }
    }

  override val date: AWSDate?
    get() {
      _response.throwIfError("date")
      return (_element["date"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalDateIso8601Serializer, value0)
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val disabled: Boolean
    get() {
      _response.throwIfError("disabled")
      return (_element["disabled"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("Event: disabled")
    }

  override val duration: EventDuration?
    get() {
      _response.throwIfError("duration")
      return (_element["duration"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(DurationSerializer, value0)
      }
    }

  override val endDate: AWSDate?
    get() {
      _response.throwIfError("endDate")
      return (_element["endDate"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalDateIso8601Serializer, value0)
      }
    }

  override val endTime: AWSTime?
    get() {
      _response.throwIfError("endTime")
      return (_element["endTime"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Event: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Event: id")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Event: images")
        }
      } ?: throw NullPointerException("Event: images")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Event: name")
    }

  override val parent: Event?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      return (_element["parking"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Event: path")
    }

  override val performers: List<Performer>
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            PerformerResponse(_response.forElement("performers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Event: performers")
        }
      } ?: throw NullPointerException("Event: performers")
    }

  override val related: EventSearchResults
    get() {
      _response.throwIfError("related")
      return (_element["related"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("related"), value0.jsonObject)
      } ?: throw NullPointerException("Event: related")
    }

  override val score: Int?
    get() {
      _response.throwIfError("score")
      return (_element["score"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val sourceUrl: String?
    get() {
      _response.throwIfError("sourceUrl")
      return (_element["sourceUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      return (_element["tagline"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Event: tags")
        }
      } ?: throw NullPointerException("Event: tags")
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      return (_element["time"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      }
    }

  override val ts: AWSDateTime?
    get() {
      _response.throwIfError("ts")
      return (_element["ts"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      }
    }

  override val venue: Venue?
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("venue"), value0.jsonObject)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface EventQuery {
  public val __typename: String

  public val dateRange: DateRange?

  public val limit: Int?

  public val minScore: Float?

  public val recentlyAnnounced: Boolean?

  public val sort: SortOrder?

  public val tags: List<String>?

  public val venues: List<String>?
}

public class EventQueryResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : EventQuery {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val dateRange: DateRange?
    get() {
      _response.throwIfError("dateRange")
      return (_element["dateRange"])?.takeIf { it !is JsonNull }?.let { value0 ->
        DateRangeResponse(_response.forElement("dateRange"), value0.jsonObject)
      }
    }

  override val limit: Int?
    get() {
      _response.throwIfError("limit")
      return (_element["limit"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val minScore: Float?
    get() {
      _response.throwIfError("minScore")
      return (_element["minScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.float
      }
    }

  override val recentlyAnnounced: Boolean?
    get() {
      _response.throwIfError("recentlyAnnounced")
      return (_element["recentlyAnnounced"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val sort: SortOrder?
    get() {
      _response.throwIfError("sort")
      return (_element["sort"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SortOrder.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val tags: List<String>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventQuery: tags")
        }
      }
    }

  override val venues: List<String>?
    get() {
      _response.throwIfError("venues")
      return (_element["venues"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventQuery: venues")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface EventSearchResults {
  public val __typename: String

  public val events: List<Event>

  public val pageToken: String?
}

public class EventSearchResultsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : EventSearchResults {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val events: List<Event>
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("events").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("EventSearchResults: events")
        }
      } ?: throw NullPointerException("EventSearchResults: events")
    }

  override val pageToken: String?
    get() {
      _response.throwIfError("pageToken")
      return (_element["pageToken"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface EventTag {
  public val __typename: String

  public val confidence: Int?

  public val id: ID

  public val name: String
}

public class EventTagResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : EventTag {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val confidence: Int?
    get() {
      _response.throwIfError("confidence")
      return (_element["confidence"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("EventTag: id")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("EventTag: name")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface EventUpdate {
  public val __typename: String

  public val aliases: List<String>?

  public val date: AWSDate?

  public val description: String?

  public val duration: Int?

  public val endDate: AWSDate?

  public val endTime: AWSTime?

  public val eventTags: List<EventTag>?

  public val excluded: Boolean?

  public val excludedImages: List<String>?

  public val excludedTags: List<String>?

  public val images: List<String>?

  public val name: String?

  public val parent: String?

  public val performers: List<String>?

  public val rankingScore: Int?

  public val sourceUrl: String?

  public val suggestedPerformers: List<String>?

  public val tagline: String?

  public val ticketsUrl: String?

  public val time: AWSTime?

  public val venue: String?
}

public class EventUpdateResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : EventUpdate {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      return (_element["aliases"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventUpdate: aliases")
        }
      }
    }

  override val date: AWSDate?
    get() {
      _response.throwIfError("date")
      return (_element["date"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalDateIso8601Serializer, value0)
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val duration: Int?
    get() {
      _response.throwIfError("duration")
      return (_element["duration"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val endDate: AWSDate?
    get() {
      _response.throwIfError("endDate")
      return (_element["endDate"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalDateIso8601Serializer, value0)
      }
    }

  override val endTime: AWSTime?
    get() {
      _response.throwIfError("endTime")
      return (_element["endTime"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      }
    }

  override val eventTags: List<EventTag>?
    get() {
      _response.throwIfError("eventTags")
      return (_element["eventTags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventTagResponse(_response.forElement("eventTags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("EventUpdate: eventTags")
        }
      }
    }

  override val excluded: Boolean?
    get() {
      _response.throwIfError("excluded")
      return (_element["excluded"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val excludedImages: List<String>?
    get() {
      _response.throwIfError("excludedImages")
      return (_element["excludedImages"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventUpdate: excludedImages")
        }
      }
    }

  override val excludedTags: List<String>?
    get() {
      _response.throwIfError("excludedTags")
      return (_element["excludedTags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventUpdate: excludedTags")
        }
      }
    }

  override val images: List<String>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventUpdate: images")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val parent: String?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val performers: List<String>?
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventUpdate: performers")
        }
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val sourceUrl: String?
    get() {
      _response.throwIfError("sourceUrl")
      return (_element["sourceUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val suggestedPerformers: List<String>?
    get() {
      _response.throwIfError("suggestedPerformers")
      return (_element["suggestedPerformers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("EventUpdate: suggestedPerformers")
        }
      }
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      return (_element["tagline"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      return (_element["time"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      }
    }

  override val venue: String?
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Events {
  public val __typename: String

  public val event: Event

  public val recentlyActive: EventSearchResults

  public val recentlyActiveByStream: EventSearchResults

  public val search: EventSearchResults
}

public class EventsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Events {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val event: Event
    get() {
      _response.throwIfError("event")
      return (_element["event"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventResponse(_response.forElement("event"), value0.jsonObject)
      } ?: throw NullPointerException("Events: event")
    }

  override val recentlyActive: EventSearchResults
    get() {
      _response.throwIfError("recentlyActive")
      return (_element["recentlyActive"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("recentlyActive"), value0.jsonObject)
      } ?: throw NullPointerException("Events: recentlyActive")
    }

  override val recentlyActiveByStream: EventSearchResults
    get() {
      _response.throwIfError("recentlyActiveByStream")
      return (_element["recentlyActiveByStream"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("recentlyActiveByStream"), value0.jsonObject)
      } ?: throw NullPointerException("Events: recentlyActiveByStream")
    }

  override val search: EventSearchResults
    get() {
      _response.throwIfError("search")
      return (_element["search"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("search"), value0.jsonObject)
      } ?: throw NullPointerException("Events: search")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface GPSCoordinates {
  public val __typename: String

  public val latitude: Float

  public val longitude: Float
}

public class GPSCoordinatesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : GPSCoordinates {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val latitude: Float
    get() {
      _response.throwIfError("latitude")
      return (_element["latitude"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.float
      } ?: throw NullPointerException("GPSCoordinates: latitude")
    }

  override val longitude: Float
    get() {
      _response.throwIfError("longitude")
      return (_element["longitude"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.float
      } ?: throw NullPointerException("GPSCoordinates: longitude")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface GlobalSearchResults {
  public val __typename: String

  public val events: List<Event>?

  public val performers: List<Performer>?

  public val results: List<Entity>?

  public val tags: List<Tag>?

  public val venues: List<Venue>?
}

public class GlobalSearchResultsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : GlobalSearchResults {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val events: List<Event>?
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("events").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("GlobalSearchResults: events")
        }
      }
    }

  override val performers: List<Performer>?
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            PerformerResponse(_response.forElement("performers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("GlobalSearchResults: performers")
        }
      }
    }

  override val results: List<Entity>?
    get() {
      _response.throwIfError("results")
      return (_element["results"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("results").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("results").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("results").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("results").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("results").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("results").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("results").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("results").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("GlobalSearchResults: results")
        }
      }
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("GlobalSearchResults: tags")
        }
      }
    }

  override val venues: List<Venue>?
    get() {
      _response.throwIfError("venues")
      return (_element["venues"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            VenueResponse(_response.forElement("venues").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("GlobalSearchResults: venues")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface HoursOverride {
  public val __typename: String

  public val closed: Boolean

  public val date: AWSDate

  public val note: String?

  public val ranges: List<OpenRange>
}

public class HoursOverrideResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : HoursOverride {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val closed: Boolean
    get() {
      _response.throwIfError("closed")
      return (_element["closed"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("HoursOverride: closed")
    }

  override val date: AWSDate
    get() {
      _response.throwIfError("date")
      return (_element["date"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalDateIso8601Serializer, value0)
      } ?: throw NullPointerException("HoursOverride: date")
    }

  override val note: String?
    get() {
      _response.throwIfError("note")
      return (_element["note"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val ranges: List<OpenRange>
    get() {
      _response.throwIfError("ranges")
      return (_element["ranges"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            OpenRangeResponse(_response.forElement("ranges").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("HoursOverride: ranges")
        }
      } ?: throw NullPointerException("HoursOverride: ranges")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Image {
  public val __typename: String

  public val height: Int?

  public val id: String

  public val large: Image?

  public val medium: Image?

  public val small: Image?

  public val uri: String

  public val variant: ImageVariant?

  public val variants: List<Image>

  public val width: Int?

  public val withWidth: Image
}

public class ImageResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Image {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val height: Int?
    get() {
      _response.throwIfError("height")
      return (_element["height"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val id: String
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Image: id")
    }

  override val large: Image?
    get() {
      _response.throwIfError("large")
      return (_element["large"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ImageResponse(_response.forElement("large"), value0.jsonObject)
      }
    }

  override val medium: Image?
    get() {
      _response.throwIfError("medium")
      return (_element["medium"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ImageResponse(_response.forElement("medium"), value0.jsonObject)
      }
    }

  override val small: Image?
    get() {
      _response.throwIfError("small")
      return (_element["small"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ImageResponse(_response.forElement("small"), value0.jsonObject)
      }
    }

  override val uri: String
    get() {
      _response.throwIfError("uri")
      return (_element["uri"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Image: uri")
    }

  override val variant: ImageVariant?
    get() {
      _response.throwIfError("variant")
      return (_element["variant"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ImageVariant.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val variants: List<Image>
    get() {
      _response.throwIfError("variants")
      return (_element["variants"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("variants").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Image: variants")
        }
      } ?: throw NullPointerException("Image: variants")
    }

  override val width: Int?
    get() {
      _response.throwIfError("width")
      return (_element["width"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val withWidth: Image
    get() {
      _response.throwIfError("withWidth")
      return (_element["withWidth"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ImageResponse(_response.forElement("withWidth"), value0.jsonObject)
      } ?: throw NullPointerException("Image: withWidth")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface InteractionSummary {
  public val __typename: String

  public val liked: Boolean

  public val path: String
}

public class InteractionSummaryResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : InteractionSummary {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val liked: Boolean
    get() {
      _response.throwIfError("liked")
      return (_element["liked"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("InteractionSummary: liked")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("InteractionSummary: path")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface LoaderExecution {
  public val __typename: String

  public val events: String

  public val ts: AWSDateTime
}

public class LoaderExecutionResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : LoaderExecution {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val events: String
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("LoaderExecution: events")
    }

  override val ts: AWSDateTime
    get() {
      _response.throwIfError("ts")
      return (_element["ts"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("LoaderExecution: ts")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface LogEvent {
  public val __typename: String

  public val message: String?

  public val ts: AWSDateTime
}

public class LogEventResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : LogEvent {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val message: String?
    get() {
      _response.throwIfError("message")
      return (_element["message"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val ts: AWSDateTime
    get() {
      _response.throwIfError("ts")
      return (_element["ts"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("LogEvent: ts")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface LogQueryField {
  public val __typename: String

  public val `field`: String

  public val `value`: String?
}

public class LogQueryFieldResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : LogQueryField {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val `field`: String
    get() {
      _response.throwIfError("field")
      return (_element["field"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("LogQueryField: field")
    }

  override val `value`: String?
    get() {
      _response.throwIfError("value")
      return (_element["value"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface LogQueryResult {
  public val __typename: String

  public val rows: List<LogQueryRow>

  public val statistics: LogQueryStatistics?

  public val status: String
}

public class LogQueryResultResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : LogQueryResult {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val rows: List<LogQueryRow>
    get() {
      _response.throwIfError("rows")
      return (_element["rows"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            LogQueryRowResponse(_response.forElement("rows").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("LogQueryResult: rows")
        }
      } ?: throw NullPointerException("LogQueryResult: rows")
    }

  override val statistics: LogQueryStatistics?
    get() {
      _response.throwIfError("statistics")
      return (_element["statistics"])?.takeIf { it !is JsonNull }?.let { value0 ->
        LogQueryStatisticsResponse(_response.forElement("statistics"), value0.jsonObject)
      }
    }

  override val status: String
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("LogQueryResult: status")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface LogQueryRow {
  public val __typename: String

  public val fields: List<LogQueryField>
}

public class LogQueryRowResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : LogQueryRow {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val fields: List<LogQueryField>
    get() {
      _response.throwIfError("fields")
      return (_element["fields"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            LogQueryFieldResponse(_response.forElement("fields").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("LogQueryRow: fields")
        }
      } ?: throw NullPointerException("LogQueryRow: fields")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface LogQueryStatistics {
  public val __typename: String

  public val recordsMatched: Float?

  public val recordsScanned: Float?
}

public class LogQueryStatisticsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : LogQueryStatistics {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val recordsMatched: Float?
    get() {
      _response.throwIfError("recordsMatched")
      return (_element["recordsMatched"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.float
      }
    }

  override val recordsScanned: Float?
    get() {
      _response.throwIfError("recordsScanned")
      return (_element["recordsScanned"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.float
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Mutation {
  public val __typename: String

  public val accounts: Accounts

  public val admin: AdminMutation

  public val clientSupport: ClientSupport

  public val subscriptions: SubscriptionsMutator
}

public class MutationResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Mutation {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val accounts: Accounts
    get() {
      _response.throwIfError("accounts")
      return (_element["accounts"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AccountsResponse(_response.forElement("accounts"), value0.jsonObject)
      } ?: throw NullPointerException("Mutation: accounts")
    }

  override val admin: AdminMutation
    get() {
      _response.throwIfError("admin")
      return (_element["admin"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AdminMutationResponse(_response.forElement("admin"), value0.jsonObject)
      } ?: throw NullPointerException("Mutation: admin")
    }

  override val clientSupport: ClientSupport
    get() {
      _response.throwIfError("clientSupport")
      return (_element["clientSupport"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ClientSupportResponse(_response.forElement("clientSupport"), value0.jsonObject)
      } ?: throw NullPointerException("Mutation: clientSupport")
    }

  override val subscriptions: SubscriptionsMutator
    get() {
      _response.throwIfError("subscriptions")
      return (_element["subscriptions"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SubscriptionsMutatorResponse(_response.forElement("subscriptions"), value0.jsonObject)
      } ?: throw NullPointerException("Mutation: subscriptions")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Onboarding {
  public val __typename: String

  public val prefSelection: List<OnboardingPref>
}

public class OnboardingResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Onboarding {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val prefSelection: List<OnboardingPref>
    get() {
      _response.throwIfError("prefSelection")
      return (_element["prefSelection"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            OnboardingPrefResponse(_response.forElement("prefSelection").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Onboarding: prefSelection")
        }
      } ?: throw NullPointerException("Onboarding: prefSelection")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface OnboardingPref {
  public val __typename: String

  public val description: String?

  public val entities: List<Entity>

  public val title: String?
}

public class OnboardingPrefResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : OnboardingPref {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val entities: List<Entity>
    get() {
      _response.throwIfError("entities")
      return (_element["entities"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("entities").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("OnboardingPref: entities")
        }
      } ?: throw NullPointerException("OnboardingPref: entities")
    }

  override val title: String?
    get() {
      _response.throwIfError("title")
      return (_element["title"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface OpenInterval {
  public val __typename: String

  public val end: AWSDateTime

  public val start: AWSDateTime
}

public class OpenIntervalResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : OpenInterval {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val end: AWSDateTime
    get() {
      _response.throwIfError("end")
      return (_element["end"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("OpenInterval: end")
    }

  override val start: AWSDateTime
    get() {
      _response.throwIfError("start")
      return (_element["start"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("OpenInterval: start")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface OpenRange {
  public val __typename: String

  public val durationMinutes: Int

  public val `open`: AWSTime
}

public class OpenRangeResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : OpenRange {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val durationMinutes: Int
    get() {
      _response.throwIfError("durationMinutes")
      return (_element["durationMinutes"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      } ?: throw NullPointerException("OpenRange: durationMinutes")
    }

  override val `open`: AWSTime
    get() {
      _response.throwIfError("open")
      return (_element["open"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, value0)
      } ?: throw NullPointerException("OpenRange: open")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface OpeningHours {
  public val __typename: String

  public val notes: String?

  public val openOn: List<OpenInterval>

  public val overrides: List<HoursOverride>

  public val weekly: List<WeeklyHours>
}

public class OpeningHoursResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : OpeningHours {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val notes: String?
    get() {
      _response.throwIfError("notes")
      return (_element["notes"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val openOn: List<OpenInterval>
    get() {
      _response.throwIfError("openOn")
      return (_element["openOn"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            OpenIntervalResponse(_response.forElement("openOn").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("OpeningHours: openOn")
        }
      } ?: throw NullPointerException("OpeningHours: openOn")
    }

  override val overrides: List<HoursOverride>
    get() {
      _response.throwIfError("overrides")
      return (_element["overrides"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            HoursOverrideResponse(_response.forElement("overrides").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("OpeningHours: overrides")
        }
      } ?: throw NullPointerException("OpeningHours: overrides")
    }

  override val weekly: List<WeeklyHours>
    get() {
      _response.throwIfError("weekly")
      return (_element["weekly"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            WeeklyHoursResponse(_response.forElement("weekly").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("OpeningHours: weekly")
        }
      } ?: throw NullPointerException("OpeningHours: weekly")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface PanelSource {
  public val __typename: String

  public val deduplicate: Boolean?

  public val entityType: SourceEntityType

  public val eventQueries: List<EventQuery>?

  public val eventQuery: EventQuery?

  public val limit: Int?

  public val manual: List<String>?

  public val minimum: Int?

  public val mode: SourceMode

  public val sort: SortOrder?
}

public class PanelSourceResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : PanelSource {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val deduplicate: Boolean?
    get() {
      _response.throwIfError("deduplicate")
      return (_element["deduplicate"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      }
    }

  override val entityType: SourceEntityType
    get() {
      _response.throwIfError("entityType")
      return (_element["entityType"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SourceEntityType.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("PanelSource: entityType")
    }

  override val eventQueries: List<EventQuery>?
    get() {
      _response.throwIfError("eventQueries")
      return (_element["eventQueries"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventQueryResponse(_response.forElement("eventQueries").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("PanelSource: eventQueries")
        }
      }
    }

  override val eventQuery: EventQuery?
    get() {
      _response.throwIfError("eventQuery")
      return (_element["eventQuery"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventQueryResponse(_response.forElement("eventQuery"), value0.jsonObject)
      }
    }

  override val limit: Int?
    get() {
      _response.throwIfError("limit")
      return (_element["limit"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val manual: List<String>?
    get() {
      _response.throwIfError("manual")
      return (_element["manual"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("PanelSource: manual")
        }
      }
    }

  override val minimum: Int?
    get() {
      _response.throwIfError("minimum")
      return (_element["minimum"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val mode: SourceMode
    get() {
      _response.throwIfError("mode")
      return (_element["mode"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SourceMode.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("PanelSource: mode")
    }

  override val sort: SortOrder?
    get() {
      _response.throwIfError("sort")
      return (_element["sort"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SortOrder.valueOf(value0.jsonPrimitive.content)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Performer : Entity {
  override val __typename: String

  override val description: String?

  override val events: EventSearchResults

  override val id: ID

  override val images: List<Image>

  public val links: List<SocialLink>

  override val name: String

  override val path: String

  public val related: EventSearchResults

  public val summary: String?

  override val tags: List<Tag>
}

public class PerformerResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Performer {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Performer: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Performer: id")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Performer: images")
        }
      } ?: throw NullPointerException("Performer: images")
    }

  override val links: List<SocialLink>
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SocialLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Performer: links")
        }
      } ?: throw NullPointerException("Performer: links")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Performer: name")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Performer: path")
    }

  override val related: EventSearchResults
    get() {
      _response.throwIfError("related")
      return (_element["related"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("related"), value0.jsonObject)
      } ?: throw NullPointerException("Performer: related")
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Performer: tags")
        }
      } ?: throw NullPointerException("Performer: tags")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface PerformerResults {
  public val __typename: String

  public val nextToken: String?

  public val performers: List<Performer>?
}

public class PerformerResultsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : PerformerResults {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val nextToken: String?
    get() {
      _response.throwIfError("nextToken")
      return (_element["nextToken"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val performers: List<Performer>?
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            PerformerResponse(_response.forElement("performers").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("PerformerResults: performers")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface PerformerUpdate {
  public val __typename: String

  public val aliases: List<String>?

  public val description: String?

  public val images: List<String>?

  public val name: String?

  public val rankingScore: Int?

  public val summary: String?

  public val tags: List<String>?

  public val wikiIdentifier: String?
}

public class PerformerUpdateResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : PerformerUpdate {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      return (_element["aliases"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("PerformerUpdate: aliases")
        }
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val images: List<String>?
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("PerformerUpdate: images")
        }
      }
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      return (_element["rankingScore"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<String>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("PerformerUpdate: tags")
        }
      }
    }

  override val wikiIdentifier: String?
    get() {
      _response.throwIfError("wikiIdentifier")
      return (_element["wikiIdentifier"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Performers {
  public val __typename: String

  public val performer: Performer

  public val search: PerformerResults
}

public class PerformersResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Performers {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val performer: Performer
    get() {
      _response.throwIfError("performer")
      return (_element["performer"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformerResponse(_response.forElement("performer"), value0.jsonObject)
      } ?: throw NullPointerException("Performers: performer")
    }

  override val search: PerformerResults
    get() {
      _response.throwIfError("search")
      return (_element["search"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformerResultsResponse(_response.forElement("search"), value0.jsonObject)
      } ?: throw NullPointerException("Performers: search")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Query {
  public val __typename: String

  public val articles: Articles

  public val auxContent: AuxContent

  public val clientSupport: ClientSupport

  public val content: Content

  public val contentViews: ContentViews

  public val conversations: Conversations

  public val entities: List<Entity>

  public val entity: Entity

  public val events: Events

  public val performers: Performers

  public val permissions: List<String>

  public val search: GlobalSearchResults

  public val subscriptions: Subscriptions

  public val tags: Tags

  public val thingsToDo: ThingsToDo

  public val typeahead: GlobalSearchResults

  public val venues: Venues
}

public class QueryResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Query {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val articles: Articles
    get() {
      _response.throwIfError("articles")
      return (_element["articles"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ArticlesResponse(_response.forElement("articles"), value0.jsonObject)
      } ?: throw NullPointerException("Query: articles")
    }

  override val auxContent: AuxContent
    get() {
      _response.throwIfError("auxContent")
      return (_element["auxContent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AuxContentResponse(_response.forElement("auxContent"), value0.jsonObject)
      } ?: throw NullPointerException("Query: auxContent")
    }

  override val clientSupport: ClientSupport
    get() {
      _response.throwIfError("clientSupport")
      return (_element["clientSupport"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ClientSupportResponse(_response.forElement("clientSupport"), value0.jsonObject)
      } ?: throw NullPointerException("Query: clientSupport")
    }

  override val content: Content
    get() {
      _response.throwIfError("content")
      return (_element["content"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentResponse(_response.forElement("content"), value0.jsonObject)
      } ?: throw NullPointerException("Query: content")
    }

  override val contentViews: ContentViews
    get() {
      _response.throwIfError("contentViews")
      return (_element["contentViews"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ContentViewsResponse(_response.forElement("contentViews"), value0.jsonObject)
      } ?: throw NullPointerException("Query: contentViews")
    }

  override val conversations: Conversations
    get() {
      _response.throwIfError("conversations")
      return (_element["conversations"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ConversationsResponse(_response.forElement("conversations"), value0.jsonObject)
      } ?: throw NullPointerException("Query: conversations")
    }

  override val entities: List<Entity>
    get() {
      _response.throwIfError("entities")
      return (_element["entities"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonObject.let { element ->
              val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
              when (typeName) {
                "Article" -> ArticleResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Event" -> EventResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Performer" -> PerformerResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Tag" -> TagResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "ThingToDo" -> ThingToDoResponse(_response.forElement("entities").forElement(index1.toString()), element)
                "Venue" -> VenueResponse(_response.forElement("entities").forElement(index1.toString()), element)
                else -> EntityResponse(_response.forElement("entities").forElement(index1.toString()), element)
              }
            }
          } ?: throw NullPointerException("Query: entities")
        }
      } ?: throw NullPointerException("Query: entities")
    }

  override val entity: Entity
    get() {
      _response.throwIfError("entity")
      return (_element["entity"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonObject.let { element ->
          val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Article" -> ArticleResponse(_response.forElement("entity"), element)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entity"), element)
            "Event" -> EventResponse(_response.forElement("entity"), element)
            "Performer" -> PerformerResponse(_response.forElement("entity"), element)
            "Tag" -> TagResponse(_response.forElement("entity"), element)
            "ThingToDo" -> ThingToDoResponse(_response.forElement("entity"), element)
            "Venue" -> VenueResponse(_response.forElement("entity"), element)
            else -> EntityResponse(_response.forElement("entity"), element)
          }
        }
      } ?: throw NullPointerException("Query: entity")
    }

  override val events: Events
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Query: events")
    }

  override val performers: Performers
    get() {
      _response.throwIfError("performers")
      return (_element["performers"])?.takeIf { it !is JsonNull }?.let { value0 ->
        PerformersResponse(_response.forElement("performers"), value0.jsonObject)
      } ?: throw NullPointerException("Query: performers")
    }

  override val permissions: List<String>
    get() {
      _response.throwIfError("permissions")
      return (_element["permissions"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            value2.jsonPrimitive.content
          } ?: throw NullPointerException("Query: permissions")
        }
      } ?: throw NullPointerException("Query: permissions")
    }

  override val search: GlobalSearchResults
    get() {
      _response.throwIfError("search")
      return (_element["search"])?.takeIf { it !is JsonNull }?.let { value0 ->
        GlobalSearchResultsResponse(_response.forElement("search"), value0.jsonObject)
      } ?: throw NullPointerException("Query: search")
    }

  override val subscriptions: Subscriptions
    get() {
      _response.throwIfError("subscriptions")
      return (_element["subscriptions"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SubscriptionsResponse(_response.forElement("subscriptions"), value0.jsonObject)
      } ?: throw NullPointerException("Query: subscriptions")
    }

  override val tags: Tags
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagsResponse(_response.forElement("tags"), value0.jsonObject)
      } ?: throw NullPointerException("Query: tags")
    }

  override val thingsToDo: ThingsToDo
    get() {
      _response.throwIfError("thingsToDo")
      return (_element["thingsToDo"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ThingsToDoResponse(_response.forElement("thingsToDo"), value0.jsonObject)
      } ?: throw NullPointerException("Query: thingsToDo")
    }

  override val typeahead: GlobalSearchResults
    get() {
      _response.throwIfError("typeahead")
      return (_element["typeahead"])?.takeIf { it !is JsonNull }?.let { value0 ->
        GlobalSearchResultsResponse(_response.forElement("typeahead"), value0.jsonObject)
      } ?: throw NullPointerException("Query: typeahead")
    }

  override val venues: Venues
    get() {
      _response.throwIfError("venues")
      return (_element["venues"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenuesResponse(_response.forElement("venues"), value0.jsonObject)
      } ?: throw NullPointerException("Query: venues")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface SocialImageStatus {
  public val __typename: String

  public val error: String?

  public val state: SocialImageState

  public val url: String?
}

public class SocialImageStatusResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : SocialImageStatus {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val error: String?
    get() {
      _response.throwIfError("error")
      return (_element["error"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val state: SocialImageState
    get() {
      _response.throwIfError("state")
      return (_element["state"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SocialImageState.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("SocialImageStatus: state")
    }

  override val url: String?
    get() {
      _response.throwIfError("url")
      return (_element["url"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface SocialLink {
  public val __typename: String

  public val locator: String

  public val type: String
}

public class SocialLinkResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : SocialLink {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val locator: String
    get() {
      _response.throwIfError("locator")
      return (_element["locator"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("SocialLink: locator")
    }

  override val type: String
    get() {
      _response.throwIfError("type")
      return (_element["type"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("SocialLink: type")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface SourceDataSearchResult {
  public val __typename: String

  public val description: String?

  public val id: ID
}

public class SourceDataSearchResultResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : SourceDataSearchResult {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("SourceDataSearchResult: id")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Subscription {
  public val __typename: String

  public val date: AWSDateTime

  public val description: String?

  public val interactionType: InteractionType

  public val name: String

  public val subscriber: String

  public val target: Entity
}

public class SubscriptionResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Subscription {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val date: AWSDateTime
    get() {
      _response.throwIfError("date")
      return (_element["date"])?.takeIf { it !is JsonNull }?.let { value0 ->
        json.decodeFromJsonElement(InstantSerializer, value0)
      } ?: throw NullPointerException("Subscription: date")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val interactionType: InteractionType
    get() {
      _response.throwIfError("interactionType")
      return (_element["interactionType"])?.takeIf { it !is JsonNull }?.let { value0 ->
        InteractionType.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("Subscription: interactionType")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Subscription: name")
    }

  override val subscriber: String
    get() {
      _response.throwIfError("subscriber")
      return (_element["subscriber"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Subscription: subscriber")
    }

  override val target: Entity
    get() {
      _response.throwIfError("target")
      return (_element["target"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonObject.let { element ->
          val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Article" -> ArticleResponse(_response.forElement("target"), element)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("target"), element)
            "Event" -> EventResponse(_response.forElement("target"), element)
            "Performer" -> PerformerResponse(_response.forElement("target"), element)
            "Tag" -> TagResponse(_response.forElement("target"), element)
            "ThingToDo" -> ThingToDoResponse(_response.forElement("target"), element)
            "Venue" -> VenueResponse(_response.forElement("target"), element)
            else -> EntityResponse(_response.forElement("target"), element)
          }
        }
      } ?: throw NullPointerException("Subscription: target")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Subscriptions {
  public val __typename: String

  public val mine: UserSubscriptions?

  public val userSubscriptions: UserSubscriptions
}

public class SubscriptionsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Subscriptions {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val mine: UserSubscriptions?
    get() {
      _response.throwIfError("mine")
      return (_element["mine"])?.takeIf { it !is JsonNull }?.let { value0 ->
        UserSubscriptionsResponse(_response.forElement("mine"), value0.jsonObject)
      }
    }

  override val userSubscriptions: UserSubscriptions
    get() {
      _response.throwIfError("userSubscriptions")
      return (_element["userSubscriptions"])?.takeIf { it !is JsonNull }?.let { value0 ->
        UserSubscriptionsResponse(_response.forElement("userSubscriptions"), value0.jsonObject)
      } ?: throw NullPointerException("Subscriptions: userSubscriptions")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface SubscriptionsMutator {
  public val __typename: String

  public val buildRecommendations: Boolean

  public val clearInteraction: Boolean

  public val interact: Boolean

  public val like: Boolean

  public val registerDevice: Boolean

  public val share: Boolean

  public val subscribe: Subscription

  public val unsubscribe: Boolean
}

public class SubscriptionsMutatorResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : SubscriptionsMutator {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val buildRecommendations: Boolean
    get() {
      _response.throwIfError("buildRecommendations")
      return (_element["buildRecommendations"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: buildRecommendations")
    }

  override val clearInteraction: Boolean
    get() {
      _response.throwIfError("clearInteraction")
      return (_element["clearInteraction"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: clearInteraction")
    }

  override val interact: Boolean
    get() {
      _response.throwIfError("interact")
      return (_element["interact"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: interact")
    }

  override val like: Boolean
    get() {
      _response.throwIfError("like")
      return (_element["like"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: like")
    }

  override val registerDevice: Boolean
    get() {
      _response.throwIfError("registerDevice")
      return (_element["registerDevice"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: registerDevice")
    }

  override val share: Boolean
    get() {
      _response.throwIfError("share")
      return (_element["share"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: share")
    }

  override val subscribe: Subscription
    get() {
      _response.throwIfError("subscribe")
      return (_element["subscribe"])?.takeIf { it !is JsonNull }?.let { value0 ->
        SubscriptionResponse(_response.forElement("subscribe"), value0.jsonObject)
      } ?: throw NullPointerException("SubscriptionsMutator: subscribe")
    }

  override val unsubscribe: Boolean
    get() {
      _response.throwIfError("unsubscribe")
      return (_element["unsubscribe"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SubscriptionsMutator: unsubscribe")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface SystemMutation {
  public val __typename: String

  public val applicationEvent: Boolean
}

public class SystemMutationResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : SystemMutation {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val applicationEvent: Boolean
    get() {
      _response.throwIfError("applicationEvent")
      return (_element["applicationEvent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("SystemMutation: applicationEvent")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Tag : Entity {
  override val __typename: String

  public val contentView: ViewConfiguration?

  override val description: String?

  override val events: EventSearchResults

  override val id: ID

  override val images: List<Image>

  override val name: String

  public val pageTitle: String

  public val parent: Tag?

  override val path: String

  public val personalize: Boolean

  public val shadow: Boolean

  override val tags: List<Tag>?
}

public class TagResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Tag {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val contentView: ViewConfiguration?
    get() {
      _response.throwIfError("contentView")
      return (_element["contentView"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ViewConfigurationResponse(_response.forElement("contentView"), value0.jsonObject)
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Tag: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Tag: id")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Tag: images")
        }
      } ?: throw NullPointerException("Tag: images")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Tag: name")
    }

  override val pageTitle: String
    get() {
      _response.throwIfError("pageTitle")
      return (_element["pageTitle"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Tag: pageTitle")
    }

  override val parent: Tag?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Tag: path")
    }

  override val personalize: Boolean
    get() {
      _response.throwIfError("personalize")
      return (_element["personalize"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("Tag: personalize")
    }

  override val shadow: Boolean
    get() {
      _response.throwIfError("shadow")
      return (_element["shadow"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("Tag: shadow")
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Tag: tags")
        }
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Tags {
  public val __typename: String

  public val root: List<Tag>

  public val tag: Tag

  public val tags: List<Tag>
}

public class TagsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Tags {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val root: List<Tag>
    get() {
      _response.throwIfError("root")
      return (_element["root"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("root").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Tags: root")
        }
      } ?: throw NullPointerException("Tags: root")
    }

  override val tag: Tag
    get() {
      _response.throwIfError("tag")
      return (_element["tag"])?.takeIf { it !is JsonNull }?.let { value0 ->
        TagResponse(_response.forElement("tag"), value0.jsonObject)
      } ?: throw NullPointerException("Tags: tag")
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Tags: tags")
        }
      } ?: throw NullPointerException("Tags: tags")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ThingToDo : Entity {
  override val __typename: String

  public val availability: String?

  override val description: String?

  override val events: EventSearchResults

  public val hours: OpeningHours

  override val id: ID

  override val images: List<Image>

  public val links: List<SocialLink>

  override val name: String

  public val openNow: Boolean

  public val parking: String?

  override val path: String

  public val priceFrom: Int?

  public val score: Int?

  public val summary: String?

  public val tagline: String?

  override val tags: List<Tag>

  public val ticketsUrl: String?

  public val typicalDuration: Int?

  public val venue: Venue?
}

public class ThingToDoResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ThingToDo {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val availability: String?
    get() {
      _response.throwIfError("availability")
      return (_element["availability"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("ThingToDo: events")
    }

  override val hours: OpeningHours
    get() {
      _response.throwIfError("hours")
      return (_element["hours"])?.takeIf { it !is JsonNull }?.let { value0 ->
        OpeningHoursResponse(_response.forElement("hours"), value0.jsonObject)
      } ?: throw NullPointerException("ThingToDo: hours")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ThingToDo: id")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ThingToDo: images")
        }
      } ?: throw NullPointerException("ThingToDo: images")
    }

  override val links: List<SocialLink>
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SocialLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ThingToDo: links")
        }
      } ?: throw NullPointerException("ThingToDo: links")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ThingToDo: name")
    }

  override val openNow: Boolean
    get() {
      _response.throwIfError("openNow")
      return (_element["openNow"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.boolean
      } ?: throw NullPointerException("ThingToDo: openNow")
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      return (_element["parking"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ThingToDo: path")
    }

  override val priceFrom: Int?
    get() {
      _response.throwIfError("priceFrom")
      return (_element["priceFrom"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val score: Int?
    get() {
      _response.throwIfError("score")
      return (_element["score"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      return (_element["summary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      return (_element["tagline"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ThingToDo: tags")
        }
      } ?: throw NullPointerException("ThingToDo: tags")
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val typicalDuration: Int?
    get() {
      _response.throwIfError("typicalDuration")
      return (_element["typicalDuration"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  override val venue: Venue?
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("venue"), value0.jsonObject)
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ThingToDoResults {
  public val __typename: String

  public val things: List<ThingToDo>
}

public class ThingToDoResultsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ThingToDoResults {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val things: List<ThingToDo>
    get() {
      _response.throwIfError("things")
      return (_element["things"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ThingToDoResponse(_response.forElement("things").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ThingToDoResults: things")
        }
      } ?: throw NullPointerException("ThingToDoResults: things")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ThingsToDo {
  public val __typename: String

  public val search: ThingToDoResults

  public val thing: ThingToDo
}

public class ThingsToDoResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ThingsToDo {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val search: ThingToDoResults
    get() {
      _response.throwIfError("search")
      return (_element["search"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ThingToDoResultsResponse(_response.forElement("search"), value0.jsonObject)
      } ?: throw NullPointerException("ThingsToDo: search")
    }

  override val thing: ThingToDo
    get() {
      _response.throwIfError("thing")
      return (_element["thing"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ThingToDoResponse(_response.forElement("thing"), value0.jsonObject)
      } ?: throw NullPointerException("ThingsToDo: thing")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface UserRecommendation {
  public val __typename: String

  public val entity: Entity

  public val events: List<Event>

  public val score: Int?
}

public class UserRecommendationResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : UserRecommendation {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val entity: Entity
    get() {
      _response.throwIfError("entity")
      return (_element["entity"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonObject.let { element ->
          val typeName = element["__typename"]?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Article" -> ArticleResponse(_response.forElement("entity"), element)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entity"), element)
            "Event" -> EventResponse(_response.forElement("entity"), element)
            "Performer" -> PerformerResponse(_response.forElement("entity"), element)
            "Tag" -> TagResponse(_response.forElement("entity"), element)
            "ThingToDo" -> ThingToDoResponse(_response.forElement("entity"), element)
            "Venue" -> VenueResponse(_response.forElement("entity"), element)
            else -> EntityResponse(_response.forElement("entity"), element)
          }
        }
      } ?: throw NullPointerException("UserRecommendation: entity")
    }

  override val events: List<Event>
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("events").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("UserRecommendation: events")
        }
      } ?: throw NullPointerException("UserRecommendation: events")
    }

  override val score: Int?
    get() {
      _response.throwIfError("score")
      return (_element["score"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.int
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface UserRecommendations {
  public val __typename: String

  public val comingUp: List<Event>

  public val entities: List<UserRecommendation>

  public val top: EventSearchResults
}

public class UserRecommendationsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : UserRecommendations {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val comingUp: List<Event>
    get() {
      _response.throwIfError("comingUp")
      return (_element["comingUp"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            EventResponse(_response.forElement("comingUp").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("UserRecommendations: comingUp")
        }
      } ?: throw NullPointerException("UserRecommendations: comingUp")
    }

  override val entities: List<UserRecommendation>
    get() {
      _response.throwIfError("entities")
      return (_element["entities"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            UserRecommendationResponse(_response.forElement("entities").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("UserRecommendations: entities")
        }
      } ?: throw NullPointerException("UserRecommendations: entities")
    }

  override val top: EventSearchResults
    get() {
      _response.throwIfError("top")
      return (_element["top"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("top"), value0.jsonObject)
      } ?: throw NullPointerException("UserRecommendations: top")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface UserSubscriptions {
  public val __typename: String

  public val calendar: EventSearchResults

  public val events: EventSearchResults

  public val interactionSummary: InteractionSummary

  public val interactions: List<Subscription>

  public val interactionsWithEntity: List<Subscription>

  public val recommendations: UserRecommendations
}

public class UserSubscriptionsResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : UserSubscriptions {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val calendar: EventSearchResults
    get() {
      _response.throwIfError("calendar")
      return (_element["calendar"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("calendar"), value0.jsonObject)
      } ?: throw NullPointerException("UserSubscriptions: calendar")
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("UserSubscriptions: events")
    }

  override val interactionSummary: InteractionSummary
    get() {
      _response.throwIfError("interactionSummary")
      return (_element["interactionSummary"])?.takeIf { it !is JsonNull }?.let { value0 ->
        InteractionSummaryResponse(_response.forElement("interactionSummary"), value0.jsonObject)
      } ?: throw NullPointerException("UserSubscriptions: interactionSummary")
    }

  override val interactions: List<Subscription>
    get() {
      _response.throwIfError("interactions")
      return (_element["interactions"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SubscriptionResponse(_response.forElement("interactions").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("UserSubscriptions: interactions")
        }
      } ?: throw NullPointerException("UserSubscriptions: interactions")
    }

  override val interactionsWithEntity: List<Subscription>
    get() {
      _response.throwIfError("interactionsWithEntity")
      return (_element["interactionsWithEntity"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SubscriptionResponse(_response.forElement("interactionsWithEntity").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("UserSubscriptions: interactionsWithEntity")
        }
      } ?: throw NullPointerException("UserSubscriptions: interactionsWithEntity")
    }

  override val recommendations: UserRecommendations
    get() {
      _response.throwIfError("recommendations")
      return (_element["recommendations"])?.takeIf { it !is JsonNull }?.let { value0 ->
        UserRecommendationsResponse(_response.forElement("recommendations"), value0.jsonObject)
      } ?: throw NullPointerException("UserSubscriptions: recommendations")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Venue : Entity {
  override val __typename: String

  public val address: Address?

  public val children: List<Venue>

  public val contentView: ViewConfiguration?

  override val description: String?

  public val eventStream: EventStream?

  override val events: EventSearchResults

  override val id: ID

  override val images: List<Image>

  public val links: List<SocialLink>

  public val location: GPSCoordinates?

  override val name: String

  public val parent: Venue?

  public val parking: String?

  override val path: String

  public val redirect: String?

  public val status: VenueStatus

  override val tags: List<Tag>

  public val thingsToDo: List<ThingToDo>

  public val ticketsUrl: String?
}

public class VenueResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Venue {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val address: Address?
    get() {
      _response.throwIfError("address")
      return (_element["address"])?.takeIf { it !is JsonNull }?.let { value0 ->
        AddressResponse(_response.forElement("address"), value0.jsonObject)
      }
    }

  override val children: List<Venue>
    get() {
      _response.throwIfError("children")
      return (_element["children"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            VenueResponse(_response.forElement("children").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Venue: children")
        }
      } ?: throw NullPointerException("Venue: children")
    }

  override val contentView: ViewConfiguration?
    get() {
      _response.throwIfError("contentView")
      return (_element["contentView"])?.takeIf { it !is JsonNull }?.let { value0 ->
        ViewConfigurationResponse(_response.forElement("contentView"), value0.jsonObject)
      }
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      return (_element["description"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val eventStream: EventStream?
    get() {
      _response.throwIfError("eventStream")
      return (_element["eventStream"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventStream.valueOf(value0.jsonPrimitive.content)
      }
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      return (_element["events"])?.takeIf { it !is JsonNull }?.let { value0 ->
        EventSearchResultsResponse(_response.forElement("events"), value0.jsonObject)
      } ?: throw NullPointerException("Venue: events")
    }

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Venue: id")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      return (_element["images"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ImageResponse(_response.forElement("images").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Venue: images")
        }
      } ?: throw NullPointerException("Venue: images")
    }

  override val links: List<SocialLink>
    get() {
      _response.throwIfError("links")
      return (_element["links"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            SocialLinkResponse(_response.forElement("links").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Venue: links")
        }
      } ?: throw NullPointerException("Venue: links")
    }

  override val location: GPSCoordinates?
    get() {
      _response.throwIfError("location")
      return (_element["location"])?.takeIf { it !is JsonNull }?.let { value0 ->
        GPSCoordinatesResponse(_response.forElement("location"), value0.jsonObject)
      }
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      return (_element["name"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Venue: name")
    }

  override val parent: Venue?
    get() {
      _response.throwIfError("parent")
      return (_element["parent"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("parent"), value0.jsonObject)
      }
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      return (_element["parking"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      return (_element["path"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("Venue: path")
    }

  override val redirect: String?
    get() {
      _response.throwIfError("redirect")
      return (_element["redirect"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  override val status: VenueStatus
    get() {
      _response.throwIfError("status")
      return (_element["status"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueStatus.valueOf(value0.jsonPrimitive.content)
      } ?: throw NullPointerException("Venue: status")
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      return (_element["tags"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            TagResponse(_response.forElement("tags").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Venue: tags")
        }
      } ?: throw NullPointerException("Venue: tags")
    }

  override val thingsToDo: List<ThingToDo>
    get() {
      _response.throwIfError("thingsToDo")
      return (_element["thingsToDo"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ThingToDoResponse(_response.forElement("thingsToDo").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("Venue: thingsToDo")
        }
      } ?: throw NullPointerException("Venue: thingsToDo")
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      return (_element["ticketsUrl"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      }
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface Venues {
  public val __typename: String

  public val root: Venue

  public val venue: Venue
}

public class VenuesResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : Venues {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val root: Venue
    get() {
      _response.throwIfError("root")
      return (_element["root"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("root"), value0.jsonObject)
      } ?: throw NullPointerException("Venues: root")
    }

  override val venue: Venue
    get() {
      _response.throwIfError("venue")
      return (_element["venue"])?.takeIf { it !is JsonNull }?.let { value0 ->
        VenueResponse(_response.forElement("venue"), value0.jsonObject)
      } ?: throw NullPointerException("Venues: venue")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface ViewConfiguration {
  public val __typename: String

  public val id: ID

  public val panels: List<ContentPanel>
}

public class ViewConfigurationResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : ViewConfiguration {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      return (_element["id"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonPrimitive.content
      } ?: throw NullPointerException("ViewConfiguration: id")
    }

  override val panels: List<ContentPanel>
    get() {
      _response.throwIfError("panels")
      return (_element["panels"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            ContentPanelResponse(_response.forElement("panels").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("ViewConfiguration: panels")
        }
      } ?: throw NullPointerException("ViewConfiguration: panels")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}

public interface WeeklyHours {
  public val __typename: String

  public val days: List<Weekday>

  public val ranges: List<OpenRange>
}

public class WeeklyHoursResponse(
  private val _response: GraphQLResponse,
  private val _element: JsonObject,
) : WeeklyHours {
  override val __typename: String
    get() = _element["__typename"]!!.jsonPrimitive.content

  override val days: List<Weekday>
    get() {
      _response.throwIfError("days")
      return (_element["days"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            Weekday.valueOf(value2.jsonPrimitive.content)
          } ?: throw NullPointerException("WeeklyHours: days")
        }
      } ?: throw NullPointerException("WeeklyHours: days")
    }

  override val ranges: List<OpenRange>
    get() {
      _response.throwIfError("ranges")
      return (_element["ranges"])?.takeIf { it !is JsonNull }?.let { value0 ->
        value0.jsonArray.mapIndexed { index1, element1 ->
          (element1)?.takeIf { it !is JsonNull }?.let { value2 ->
            OpenRangeResponse(_response.forElement("ranges").forElement(index1.toString()), value2.jsonObject)
          } ?: throw NullPointerException("WeeklyHours: ranges")
        }
      } ?: throw NullPointerException("WeeklyHours: ranges")
    }

  public fun hasField(key: String): Boolean = _element.containsKey(key)
}
